<template>
  <div
    ref="padContainer"
    class="pad-container"
  >
    <span
      ref="sanitized"
      class="hidden"
    />
    <Modal
      v-model="snowflakeConfigPage"
      class="snowflake-modal"
      :mask-closable="false"
    >
      <p
        slot="header"
        class="snowflake credentials-page-info"
      >
        <Icon type="custom-snowflake" />
        <span>Snowflake Credentials</span>
      </p>
      <div class="snowflake-input-container">
        <Form
          ref="snowflakeCredentialsForm"
          :model="snowflakeCredentials"
          :rules="snowflakeLogInRule"
        >
          <FormItem prop="username">
            <i-input
              v-model="snowflakeCredentials.username"
              type="text"
              placeholder="Username"
            >
              <Icon
                slot="prepend"
                type="ios-person-outline"
              />
            </i-input>
          </FormItem>
          <FormItem prop="password">
            <i-input
              v-model="snowflakeCredentials.password"
              type="password"
              placeholder="Password"
            >
              <Icon
                slot="prepend"
                type="ios-lock-outline"
              />
            </i-input>
          </FormItem>
          <FormItem prop="role">
            <i-input
              v-model="snowflakeCredentials.role"
              type="text"
              placeholder="Role (Optional)"
            >
              <Icon
                slot="prepend"
                type="ios-eye-outline"
              />
            </i-input>
          </FormItem>
          <FormItem prop="warehouse">
            <i-input
              v-model="snowflakeCredentials.warehouse"
              type="text"
              placeholder="Warehouse (Optional)"
            >
              <Icon
                slot="prepend"
                type="ios-home-outline"
              />
            </i-input>
          </FormItem>
          <FormItem prop="database">
            <i-input
              v-model="snowflakeCredentials.database"
              type="text"
              placeholder="Database (Optional)"
            >
              <Icon
                slot="prepend"
                type="ios-cloud-outline"
              />
            </i-input>
          </FormItem>
        </Form>
      </div>
      <div slot="footer">
        <Button
          type="success"
          size="large"
          style="width: 48%"
          :loading="snowflakeLogInLoading"
          @click="setSnowflakeConfig('snowflakeCredentialsForm')"
        >
          OK
        </Button>
        <Button
          type="info"
          size="large"
          style="width: 48%"
          :loading="snowflakeLogInLoading"
          @click="setDefaultSnowflakeConfig()"
        >
          Default
        </Button>
      </div>
    </Modal>

    <ScheduleQueryModal
      v-model="showScheduleQueryModal"
      :query-id="currentTab.queryId"
      :new-schedule-query-data="newScheduleQueryData"
      @close="closeScheduleQueryModal"
    />

    <Modal
      v-model="existingCode.visible"
      :mask-closable="false"
    >
      <p
        slot="header"
        class="credentials-page-info"
        :class="existingCode.class"
      >
        <Icon
          :type="existingCode.icon"
          size="22"
        />
        <span style="font-size: 16px">{{ existingCode.title }}</span>
      </p>
      <div>
        <p style="font-size: 14px">
          {{ existingCode.content }}
        </p>
      </div>
      <div
        slot="footer"
        class="footer-buttons"
      >
        <Button
          type="success"
          size="large"
          class="copy-action-button"
          @click="onCopyQueryToPad({ queryId: selectedQuery.queryId || null, queryText: existingCode.queryText, isQueryWorkspace: false }, false, { createNewTab: true })"
        >
          New tab
        </Button>
        <Button
          type="warning"
          size="large"
          class="copy-action-button"
          @click="onCopyQueryToPad({ queryId: selectedQuery.queryId || null, queryText: existingCode.queryText, isQueryWorkspace: false }, false, { createNewTab: false })"
        >
          Override
        </Button>
        <Button
          type="default"
          size="large"
          class="copy-action-button"
          @click="existingCode.visible = false"
        >
          Cancel
        </Button>
      </div>
    </Modal>

    <Modal
      v-model="renameTabWindow.visible"
      :mask-closable="false"
      @on-visible-change="onRenameTabWindowChange"
    >
      <p
        slot="header"
        class="credentials-page-info"
      >
        <i
          :class="renameTabWindow.icon"
          class="button-icon"
          style="font-size: 16px"
        />
        <span style="font-size: 16px">{{ renameTabWindow.title }}</span>
      </p>
      <div>
        <p v-show="renameTabWindow.isSaveQuery">
          <strong>Directory:</strong> {{ selectedFileNode.folderPath }}
        </p>
        <i-input
          ref="renameTabInput"
          v-model="renameTabWindow.tabName"
          type="text"
          :placeholder="renameTabWindow.placeholder"
          @keyup.enter.native="!renameTabWindow.tabName.includes('/') && renameTabWindow.tabName.trim() && renameTab()"
        />
        <p
          v-show="renameTabWindow.errorMessage || renameTabWindow.tabName.includes('/')"
          class="error"
        >
          <span v-if="renameTabWindow.tabName.includes('/')">
            Slashes are not allowed!
          </span>
          <span v-else>
            {{ renameTabWindow.errorMessage }}
          </span>
        </p>
      </div>
      <div
        slot="footer"
        class="footer-buttons"
      >
        <Button
          type="success"
          size="large"
          style="width: 80px"
          :disabled="renameTabWindow.tabName.includes('/') || !renameTabWindow.tabName.trim()"
          @click="!renameTabWindow.tabName.includes('/') && renameTabWindow.tabName.trim() && renameTab()"
        >
          {{ renameTabWindow.okButtonLabel }}
        </Button>
        <Button
          type="default"
          size="large"
          style="width: 80px"
          @click="renameTabWindow.visible = false"
        >
          {{ renameTabWindow.cancelButtonLabel }}
        </Button>
      </div>
    </Modal>

    <Modal
      v-model="interactionWindow.visible"
      :mask-closable="false"
      class="interaction-window"
    >
      <p
        slot="header"
        class="interaction-window-title"
      >
        <i
          class="window-icon"
          :class="interactionWindow.icon"
        />
        <span class="window-title">{{ interactionWindow.title }}</span>
      </p>
      <div>
        <p
          v-dompurify-html="interactionWindow.message"
          class="interaction-window-message"
        />
        <i-input
          v-show="interactionWindow.placeholder.length"
          ref="interactionWindowInputName"
          v-model="interactionWindow.inputName"
          type="text"
          :placeholder="interactionWindow.placeholder"
          @keyup.enter.native="interactionWindow.inputName.trim().length > 0 && executeAction(interactionWindow.confirmOption)"
        />
        <p
          v-show="interactionWindow.hasError"
          class="error"
        >
          {{ interactionWindow.errorMessage }}
        </p>
      </div>
      <div
        slot="footer"
        class="footer-buttons"
      >
        <Button
          v-show="interactionWindow.confirmLabel"
          type="success"
          size="large"
          style="width: 80px"
          :disabled="interactionWindow.placeholder.length > 0 && interactionWindow.inputName.trim().length === 0"
          @click="(interactionWindow.placeholder.length === 0 || interactionWindow.inputName.trim().length > 0) && executeAction(interactionWindow.confirmOption)"
        >
          {{ interactionWindow.confirmLabel }}
        </Button>
        <Button
          v-show="interactionWindow.cancelLabel"
          type="default"
          size="large"
          style="width: 80px"
          @click="interactionWindow.visible = false"
        >
          {{ interactionWindow.cancelLabel }}
        </Button>
      </div>
    </Modal>

    <FileTreeModal
      v-model="fileTreeSelector.visible"
      :user-id="userId"
      :query-workspace="queryWorkspace"
      :query-repo="queryRepo"
      :query-repo-mode="isFileTreeQueryRepoMode"
      :folder-mode="isFileTreeFolderMode"
      :query-obj="fileTreeSelector.queryObj"
      @onSave="onFileTreeModalSave"
      @onReplace="onFileTreeModalReplace"
      @onClose="onFileTreeModalClose"
    />

    <PolygonDrawingModal
      :is-drawing-polygon="drawingPolygon"
      @patchGeospatialConditions="onPatchingGeospatialConditions"
      @exportSampleGeospatialQuery="onExportSampleGeospatialQuery"
      @isDrawingPolygon="onPolygonDrawing"
    />

    <CustomModal
      v-model="modalMixin.visible"
      :closable="modalMixin.closable"
      :mask-closable="modalMixin.maskClosable"
      :category="modalMixin.category"
      :title="modalMixin.title"
      :icon="modalMixin.icon"
      :message="modalMixin.message"
      :action-buttons="modalMixin.actionButtons"
      :cancel-button="modalMixin.cancelButton"
      @onButtonClick="executeAction"
      @closeModal="closeActionWindow"
    />

    <div v-if="shareMode">
      <SharedQuery
        :query-obj="sharedQueryData"
        :snowflake-config="snowflakeConfig"
        :shared-query-file-name="sharedQueryFileName"
        style="padding-top: 36px"
      />
    </div>

    <div v-else>
      <QueryMenuBar
        ref="menuBar"
        :current-tab="currentTab"
        :clipboard-data="clipboardData"
        :show-tool-bar="showToolBar"
        :show-action-panel="showActionPanel"
        :show-lint="cmOptions.lint"
        :show-hints="showHints"
        :theme="cmOptions.theme"
        @menuClick="onMenuClick"
      />

      <div class="tab-layout">
        <div
          v-show="showActionPanel"
          ref="query-panel"
          class="action-panel"
          :style="{ width: `${queryPanelWidth}px` }"
        >
          <QueryPanel
            :user-id="userId"
            :query-history="queryHistory"
            :query-history-shown="queryHistoryShown"
            :query-history-loading="queryHistoryLoading"
            :history-queries-displayed="historyQueriesDisplayed"
            :history-item-height="historyItemHeight"
            :panel-height="contentHeight + 2"
            :snowflake-data="snowflakeData"
            :users="users"
            :query-panel-width="queryPanelWidth"
            @copyQueryToPad="onCopyQueryToPad"
            @loadQueryHistory="onLoadQueryHistory"
            @onRelocateQueryFile="onRelocateQueryFile"
            @schemaPath="addSchemaPath"
          />
        </div>
        <div
          v-show="showActionPanel"
          ref="editorHandleVertical"
          class="handle vertical"
          @mousedown="onHandleMouseDownHorizontal"
        />
        <div
          ref="tabs-content"
          class="content-panel"
          :style="{width: `${contentWidth}px`}"
        >
          <div class="query-tabs">
            <Tabs
              :key="tabs.length"
              v-model="currentTabIndex"
              type="card"
              name="query-tabs"
              :animated="false"
              :before-remove="beforeRemoveTab"
              @contextmenu.native.prevent="onOpenContextMenu"
              @on-click="onTabSelected"
            >
              <TabPane
                v-for="(tab, index) in tabs"
                :key="index"
                tab="query-tabs"
                :label="h => tabLabelRender(h, tab)"
                :icon="tab.icon"
                :closable="tabs.length > 1"
                :style="{ maxHeight: `${contentHeight}px`, overflow: 'auto' }"
              />
              <TabPane label="+" />
            </Tabs>
            <VueContext ref="contextMenu">
              <li>
                <p @click.prevent="onMenuClick('save')">
                  Save Current Tab
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('saveAs')">
                  Save Current Tab As...
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('saveToRepo')">
                  Save Current Tab to Query Repo
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('saveAll')">
                  Save All Tabs
                </p>
              </li>
              <li class="separator" />
              <li>
                <p @click.prevent="onMenuClick('closeQuery')">
                  Close Current Tab
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('closeOthers')">
                  Close Other Tabs except Current
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('closeSaved')">
                  Close Saved Tabs
                </p>
              </li>
              <li>
                <p @click.prevent="onMenuClick('closeAll')">
                  Close All Tabs
                </p>
              </li>
            </VueContext>
            <!--
              TODO: Move codemirror inside TabPane and apply codemirror height to auto according with
              Jiaying's suggestion.
              See: https://codemirror.net/demo/resize.html
            -->
            <template v-for="(tab, index) in tabs">
              <div
                v-show="index === currentTabIndex"
                :key="index"
              >
                <codemirror
                  :ref="tab.reference"
                  v-model="tab.editorText"
                  :options="cmOptions"
                  class="query-editor"
                  :style="{ height: `${codeMirrorHeight}px` }"
                  @ready="onCmReady(index, arguments[0])"
                  @changes="debounceLocalStorage"
                />
                <div
                  v-if="tab.showTable"
                  ref="editorHandle"
                  class="handle horizontal"
                  @mousedown="onHandleMouseDown"
                />
                <QueryTable
                  v-if="tab.showTable"
                  :ref="`queryResults_${index}`"
                  class="query-table"
                  :index="index"
                  :query-id="tab.queryId"
                  :query="tab.query"
                  :query-name="tab.label"
                  :results="tab.results"
                  :has-query-run="tab.hasQueryRun"
                  :snowflake-config="snowflakeConfig"
                  :context="context"
                  :show-action="false"
                  @outcome="updateResults"
                  @runningQuery="onRunningQueryChange"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QueryTable from './helper-components/QueryTable'
import QueryPanel from './helper-components/query-pad/QueryPanel'
import QueryMenuBar from './helper-components/query-pad/QueryMenuBar'
import SharedQuery from './helper-components/SharedQuery'
import CustomModal from './helper-components/CustomModal'
import PolygonDrawingModal from './helper-components/PolygonDrawingModal'
import ScheduleQueryModal from './helper-components/scheduled-query/ScheduleQueryModal'
import FileTreeModal from './helper-components/FileTreeModal'
import axios from 'axios'
import _ from 'lodash'
import { setUpQueryFormat } from '@/helpers/query-formatter'
import { SQLLint, KEYWORD, STATEMENT_UNSUPPORTED } from '@/helpers/sqlLint'
import { customModalMixin } from '@/mixins/customModalMixin'
import UserMixin from '@/mixins/UserMixin'
import { SUCCESS, WARNING, DEFAULT } from '@/constants/buttons'
import moment from 'moment-timezone'
import { mapState } from 'vuex'
import { VueContext } from 'vue-context'
import 'codemirror/addon/lint/lint.js'
import '@/helpers/sqlLintRegister'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/show-hint'
import 'codemirror/addon/search/search'
import 'codemirror/addon/search/searchcursor'
import 'codemirror/addon/search/jump-to-line'
import 'codemirror/addon/comment/comment'
import 'codemirror/addon/dialog/dialog'
import 'codemirror/addon/dialog/dialog.css'
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/hint/sql-hint.js'
import 'codemirror/addon/hint/anyword-hint'
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/display/fullscreen'

import 'codemirror/theme/duotone-light.css'
import 'codemirror/theme/elegant.css'
import 'codemirror/theme/mdn-like.css'
import 'codemirror/theme/neat.css'

import 'codemirror/theme/abcdef.css'
import 'codemirror/theme/blackboard.css'
import 'codemirror/theme/cobalt.css'
import 'codemirror/theme/colorforth.css'
import 'codemirror/theme/dracula.css'
import 'codemirror/theme/mbo.css'
import 'codemirror/theme/midnight.css'
import 'codemirror/theme/twilight.css'

import '@/styles/query-pad-themes.css'

const QUERY_REPO_MODE = 'query-repo-mode'
const FOLDER_MODE = 'folder-mode'

export default {
  components: {
    QueryTable,
    QueryPanel,
    QueryMenuBar,
    SharedQuery,
    CustomModal,
    PolygonDrawingModal,
    VueContext,
    FileTreeModal,
    ScheduleQueryModal
  },

  mixins: [
    customModalMixin,
    UserMixin
  ],

  props: {
    userId: Number,
    goldenQuery: String, // The initial query (from Golden Query Library)
    layoutHeights: Object,
    contentTop: Number
  },

  data () {
    return {
      isQueryRunning: false,
      editorText: this.goldenQuery || '',
      query: '',
      queryName: '',
      snowflakeConfigPage: false,
      displayQueryText: false,
      displayCopyWarning: false,
      snowflakeLogInLoading: false,
      snowflakeCredentials: {
        username: '',
        password: '',
        role: '',
        database: '',
        warehouse: ''
      },
      snowflakeLogInRule: {
        username: [
          {
            required: true,
            message: 'Required',
            trigger: 'blur'
          }
        ],
        password: [
          {
            required: true,
            message: 'Required',
            trigger: 'blur'
          }
        ]
      },
      lastQueryRunAt: null,
      snowflakeConfig: null,
      context: 'query_pad',
      editor: null,
      windowWidth: 0,
      bodyMinHeight: 530,
      editorMinHeight: 150,
      contentHeight: 0,
      editorHeight: 0,
      horizontalSpace: 48 + 48 + 16, // layout {content margin-left: 48px, content margin-right: 48px, handle vertical: 16px
      queryPanelMinWidth: 258,
      startX: 0,
      startY: 0,
      startH: 0,
      startW: 0,
      cmOptions: {
        theme: this.$root.preferences.queryPad.cmTheme || 'default',
        tabSize: 4,
        indentUnit: 4,
        autofocus: true,
        smartIndent: true,
        matchBrackets: true,
        closeBrackets: true,
        autoCloseBrackets: true,
        placeholder: `Only one query can run at a time... The query at the cursor's last position will run.`,
        extraKeys: {
          'Ctrl-Space': 'autocomplete',
          'Cmd-Space': 'autocomplete',
          'Ctrl-Enter': this.runQuery,
          'Cmd-Enter': this.runQuery,
          'Ctrl-E': () => { this.toggleOption('hints') },
          'Cmd-E': () => { this.toggleOption('hints') },
          'Ctrl-S': () => { this.onSaveQuery(!this.currentTab.isQueryWorkspace) },
          'Cmd-S': () => { this.onSaveQuery(!this.currentTab.isQueryWorkspace) },
          'Ctrl-I': () => { this.formatQuery() },
          'Cmd-I': () => { this.formatQuery() },
          'Ctrl-F': () => { this.findReplace('find') },
          'Ctrl-G': () => { this.findReplace('findNext') },
          'Shift-Ctrl-G': () => { this.findReplace('findPrev') },
          'Shift-Ctrl-F': () => { this.findReplace('replace') },
          'Shift-Ctrl-R': () => { this.findReplace('replaceAll') },
          'Cmd-/': () => { this.toggleLineComment(this.currentTabIndex) },
          'Ctrl-/': () => { this.toggleLineComment(this.currentTabIndex) },
          'Shift-Cmd-/': () => { this.toggleBlockComment(this.currentTabIndex) },
          'Shift-Ctrl-/': () => { this.toggleBlockComment(this.currentTabIndex) }
        },
        mode: { name: 'text/x-sql', globalVars: true },
        lineNumbers: true,
        autoRefresh: true,
        indentWithTabs: true,
        gutters: ['CodeMirror-lint-markers'],
        lint: false
      },
      cmPreviewOptions: {
        theme: 'default',
        tabSize: 4,
        indentUnit: 4,
        smartIndent: true,
        matchBrackets: true,
        closeBrackets: true,
        mode: { name: 'text/x-sql', globalVars: true },
        lineNumbers: true,
        autoRefresh: true,
        readOnly: 'nocursor'
      },
      cmThemes: {
        light: [
          'default',
          'duotone-light',
          'elegant',
          'mdn-like',
          'neat'
        ],
        dark: [
          'abcdef',
          'blackboard',
          'cobalt',
          'colorforth',
          'dracula',
          'mbo',
          'midnight',
          'twilight'
        ]
      },
      showActionPanel: this.$root.preferences.queryPad.hasOwnProperty('isShowActionPanel') ? this.$root.preferences.queryPad.isShowActionPanel : true,
      queryPanelLabel: 'Show Query Panel',
      existingCode: {
        visible: false,
        icon: '',
        title: '',
        content: '',
        queryText: ''
      },
      renameTabWindow: {
        title: '',
        icon: '',
        visible: false,
        tabName: '',
        placeholder: '',
        okButtonLabel: '',
        cancelButtonLabel: '',
        isSaveQuery: false,
        errorMessage: null
      },
      fileTreeSelector: {
        visible: false,
        queryObj: null,
        mode: ''
      },
      tabs: [],
      currentTabIndex: 0,
      workspaceQueryIds: {},
      queryHistory: [],
      historyQueryIds: {},
      queryHistoryShown: [],
      historyQueriesDisplayed: 0,
      queryLoadLimit: 0,
      queryLoadMin: 10,
      sharedQueryData: {},
      sharedQueryResult: [],
      isSharedQuery: false,
      shareMode: !!this.$route.params.fileName,
      sharedQueryFileName: this.$route.params.fileName,
      tabClick: {
        index: null,
        click: false,
        delay: 1000,
        timer: null
      },
      showHints: this.$root.preferences.queryPad.hasOwnProperty('showHints') ? this.$root.preferences.queryPad.showHints : true,
      clipboardData: '',
      isCut: false,
      selectedQuery: {},
      workspaceData: {
        newQuery: null,
        queryUpdated: null,
        newFolderName: '',
        removeFolder: false
      },
      showToolBar: this.$root.preferences.queryPad.hasOwnProperty('showToolBar') ? this.$root.preferences.queryPad.showToolBar : true,
      interactionWindow: {
        visible: false,
        icon: '',
        title: '',
        message: '',
        inputName: '',
        placeholder: '',
        confirmLabel: '',
        cancelLabel: '',
        onCancel: '',
        hasError: false,
        errorMessage: ''
      },
      historyItemHeight: 22,
      queryWorkspaceLoading: true,
      queryHistoryLoading: true,
      sharedQuery: {},
      sharedQueryTabIndex: -1,
      snowflakeLastUpdatedDateTime: null,
      selectedTabsIndexArray: [],
      drawingPolygon: false, // Drawing Polygon modal show boolean
      isHintShown: false,
      fileTreeSelectorArray: [],
      showScheduleQueryModal: false,
      newScheduleQueryData: {
        name: '',
        schedule: '',
        recurrence: 'Daily',
        day: new Date().getDay(),
        hourAndMinute: '00:00',
        text: '',
        emailSubscribers: [],
        slackSubscribers: [],
        hasResults: false
      },
      queryPanelWidth: this.$root.preferences.queryPad.queryPanelWidth || 258
    }
  },

  computed: {
    currentTab () {
      return this.tabs[this.currentTabIndex]
    },

    contentWidth () {
      const workArea = this.windowWidth - this.horizontalSpace
      return this.showActionPanel ? workArea - this.queryPanelWidth : workArea + 16
    },

    isFileTreeQueryRepoMode () {
      const { mode } = this.fileTreeSelector

      return mode === QUERY_REPO_MODE
    },

    isFileTreeFolderMode () {
      const { mode } = this.fileTreeSelector

      return mode === FOLDER_MODE
    },

    codeMirrorHeight () {
      return this.currentTab.editorHeight || this.editorHeight
    },

    allowedKeywords () {
      return Object.values(_.omit(KEYWORD, Object.keys(STATEMENT_UNSUPPORTED)))
    },

    /**
     * Fetches the data of the first existing element in "fileTreeSelectorArray", which contains the list
     * of all new unsaved queries when the "Save All Tabs" function is triggered.
     */
    currentFileTreeSelector () {
      if (!this.fileTreeSelectorArray.length) {
        return {}
      }

      return this.fileTreeSelectorArray[0]
    },

    ...mapState('snowflake', {
      snowflakeData: state => state.snowflakeData,
      snowflakeTables: (state, getters) => getters.snowflakeTables,
      snowflakeBlueprint: (state, getters) => getters.snowflakeBlueprint,
      snowflakeLastUpdated: (state, getters) => getters.snowflakeLastUpdated
    }),

    ...mapState('queryRepo', {
      queryRepo: (state, getters) => getters.queryRepo,
      queryRepoFiles: state => state.files,
      queryRepoFolders: state => state.folders
    }),

    ...mapState('queryWorkspace', {
      queryWorkspace: state => state.workspace
    }),

    ...mapState('queryPad', {
      selectedFileNode: state => state.selectedFileNode || {}
    }),

    ...mapState('scheduledQuery', {
      scheduledQueries: 'scheduledQueries'
    })
  },

  watch: {
    $route (newRoute) {
      if (newRoute.path.includes('/query-pad')) {
        this.setupWorkArea()
      }
    },

    contentHeight (newHeight, oldHeight) {
      if (newHeight > oldHeight) {
        newHeight += 2
        if (this.calculateQueryLimit(newHeight) > this.historyQueriesDisplayed) {
          this.queryHistoryLoading = true
          this.debounceLoadQueryHistory()
        }
      }
    },

    snowflakeTables (newSnowflakeTables) {
      window.snowflakeTables = newSnowflakeTables
    },

    async snowflakeLastUpdatedDateTime (newDateTime) {
      const vueX = {
        snowflakeData: this.$store.state.snowflake.snowflakeData,
        lastUpdated: this.$store.getters['snowflake/snowflakeLastUpdated']
      }

      // If there is no VueX data for Snowflake or if the existing value for Last Updated is different
      // of the current last updated datetime for Snowflake JSON file, then update the VueX data.
      if (!vueX.snowflakeData || vueX.lastUpdated !== newDateTime) {
        await this.$store.dispatch('snowflake/fetchSnowflakeData')
      }

      this.cmOptions.lint = this.$root.preferences.queryPad.hasOwnProperty('showLint')
        ? this.$root.preferences.queryPad.showLint
        : false
    },

    userId () {
      this.$store.dispatch('scheduledQuery/onLoadScheduledQueries', this.userId)
      this.$store.dispatch('scheduledQuery/onLoadScheduledQueriesSubscriptions', this.userId)
      this.fetchQueryInfo()
    },

    contentTop () {
      this.setPanelHeight()
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('queryPad')) {
      this.$set(this.$root.preferences, 'queryPad', {})
    }
    sessionStorage.setItem('context', 'query_pad')

    if (localStorage.queryEditors) {
      localStorage.setItem('storedTabs', localStorage.queryEditors)
      localStorage.removeItem('queryEditors')
    } else if (!localStorage.storedTabs) {
      localStorage.setItem('storedTabs', '[]')
    }
  },

  created () {
    this.getSnowflakeLastUpdated()
    this.setupWorkArea()
  },

  async mounted () {
    this.setPanelHeight()
    window.addEventListener('resize', this.setPanelHeight)

    // If there is a Shared Query to display and it already exists in the tab panel, then focus it.
    if (this.$route.params.id && this.$route.params.fileName) {
      this.fetchQuery(this.$route.params.id).then(query => {
        this.sharedQueryData = query.data[0]
      })
    } else if (this.$route.params.id) {
      this.$router.replace({ name: 'Query Pad' })

      if (this.sharedQueryTabIndex >= 0) {
        this.selectTab(this.sharedQueryTabIndex)
      }
    }

    this.queryLoadLimit = this.calculateQueryLimit(this.contentHeight)
    this.fetchAllUsers()

    if (this.userId) {
      this.fetchQueryInfo()
    }
  },

  methods: {
    /**
     * Triggers a custom context menu for Tabs.
     * @param {Object} event Mouse event
     */
    onOpenContextMenu (event) {
      // Hide the context menu in case it has been triggered before.
      this.$refs.contextMenu.show = false

      // Display the context menu.
      this.$refs.contextMenu.show = true

      // Set the top and left margin
      this.$refs.contextMenu.style.top = '8px'
      this.$refs.contextMenu.style.left = `${event.clientX - 32}px`
    },

    /**
     * Sets the panel height according with the window (browser) height.
     */
    setPanelHeight () {
      if (this.shareMode) {
        return
      }

      this.windowWidth = window.innerWidth
      const menuBarHeight = this.$refs.menuBar ? this.$refs.menuBar.$el.offsetHeight : 40
      const { notice, header, footer } = this.layoutHeights
      const bodyHeight = window.innerHeight - header - footer
      const resultsObject = this.$refs[`queryResults_${this.currentTabIndex}`] ? this.$refs[`queryResults_${this.currentTabIndex}`][0] : null

      this.contentHeight = Math.max(bodyHeight, this.bodyMinHeight) - notice - menuBarHeight
      this.editorHeight = this.contentHeight - 29 // 29px is the height of the tabs.

      if (resultsObject) {
        const newHeight = this.editorMinHeight + resultsObject.$el.clientHeight + 16
        this.contentHeight = this.contentHeight < newHeight ? newHeight : this.contentHeight
      }
    },

    /**
     * Updates the panel height on window resize.
     * NOTE: $nextTick is used in order to reflect the changes immediately on window resize.
     */
    resizeHeight () {
      this.$nextTick(() => {
        this.setPanelHeight()
      })
    },

    /**
     * Fetches the last updated datetime of the Snowflake metadata and stores it into
     * "snowflakeLastUpdatedDateTime" var.
     */
    async getSnowflakeLastUpdated () {
      const timeZone = 'America/Los_Angeles'
      const jsonData = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/last-updated-date`, {
        params: {
          data: 'snowflake'
        }
      })).data

      this.snowflakeLastUpdatedDateTime = moment(jsonData.lastUpdated).tz(timeZone).format('MMM-DD-YYYY hh:mm A z')
    },

    /**
     * Render Function used for the tab labels
     * @param {Function} h - Vue/Iview render function
     * @param {Object} tab - Tab data object
     * @return {Object} - HTML object for tab label
     */
    tabLabelRender (h, tab) {
      const { label, isRepo } = tab
      const childElements = []

      if (isRepo) {
        childElements.push(h('i', {
          class: 'fab fa-google-drive',
          style: {
            'margin-right': '8px'
          }
        }))
      }

      childElements.push(h('span', label))

      return h('span', childElements)
    },

    /**
     * Handler for when use clicks save on File Tree Modal
     * @param {String} fileName - The name of the file being saved
     * @param {Object} folder - The folder in which the file is being saved under
     */
    onFileTreeModalSave (fileName, folder) {
      const { mode } = this.fileTreeSelector

      switch (mode) {
        case FOLDER_MODE:
          this.saveQueryFolder(fileName, folder)
          break
        case QUERY_REPO_MODE:
        default:
          this.saveQueryFile(fileName, folder)
      }
    },

    /**
     * Handler for when user chooses to replace a query file via file tree modal
     * @param {Object} queryToReplace - The query that is being replaced
     */
    async onFileTreeModalReplace (queryToReplace) {
      const { id, queryName, userId, folderId } = queryToReplace
      const query = {
        id,
        queryName,
        queryText: this.currentTab.editorText,
        userId,
        folderId
      }

      try {
        await this.updateQueryRepoFile(queryToReplace, query)
        this.updateCurrentTab(query, true)
      } catch (error) {
        const message = `Error while replacing file: ${error}`
        console.error(message)
        this.$Message.error(message)
      }
    },

    /**
     * Saves Query Folder on the local/shared workspace
     * @param {String} folderName - The name of the folder being saved
     * @param {Object} parentFolder - The containing folder in which the folder is saved under
     */
    async saveQueryFolder (folderName, parentFolder) {
      try {
        if (parentFolder.linkedToRepo) {
          const userFolder = await this.getQueryRepoUserFolder()
          const folder = { name: folderName, userId: this.userId }

          await this.addQueryRepoFolder(folder, userFolder.id)
          this.onFileTreeModalClose()
        } else if (parentFolder.isRepo) {
          const folder = { name: folderName }
          const parentId = parentFolder.id

          await this.addQueryRepoFolder(folder, parentId)
          this.onFileTreeModalClose()
        } else {
          const { filePath } = parentFolder
          const filePathArr = filePath.split('/')

          if (filePath.slice(-1) === '/') {
            filePathArr.pop()
          }

          this.addWorkspaceFolder(folderName, filePathArr)
          this.onFileTreeModalClose()
        }
      } catch (error) {
        console.error(`Error while saving Query Folder: ${error}`)
        this.$Message.error({
          content: 'Error while saving query folder!',
          duration: 3
        })
      }
    },

    /**
     * Adds a file to the local query workspace
     * @param {String} queryName - The name of the query
     * @param {Object} tab - Tab object to be saved.
     */
    async addQueryWorkspaceFile (queryName, tab) {
      try {
        const queryText = tab.editorText
        const savedAt = new Date().toISOString()
        const addedFile = await this.$store.dispatch(
          'queryWorkspace/addQueryWorkspaceFile',
          {
            file: {
              queryName,
              queryText,
              savedAt
            },
            userId: this.userId
          }
        )

        this.afterAddQueryFile(addedFile, tab)
      } catch (error) {
        console.error(`Error while saving Local Query Workspace File with text, ${tab.editorText}. Error: ${error.message}`)
        this.$Message.error({
          content: `Error while saving query workspace file!`,
          duration: 3
        })
      }
    },

    /**
     * Updates a query in the local workspace
     * @param {Object} query - Query Object to be saved
     * @param {Object} tab - Tab object to be saved.
     */
    async updateQueryWorkspaceFile (query, tab = this.currentTab) {
      const {
        queryId,
        queryName,
        queryText
      } = query
      await this.$store.dispatch(
        'queryWorkspace/updateQueryWorkspaceFile',
        {
          file: {
            id: queryId,
            queryName,
            queryText
          },
          userId: this.userId
        }
      )
      tab.icon = ''
      this.updateLocalStorage(tab)

      if (Object.values(this.scheduledQueries).find((scheduledQuery) => { return scheduledQuery.id === queryId })) {
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueries', this.userId)
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueriesSubscriptions', this.userId)
      }
    },

    /**
     * Removes a query from the local workspace
     * @param {Number} queryId - The id of the query being removed
     * @param {Boolean} relocatedToRepo - True if the removal of query is
     * a result of being relocated to Shared Workspace
     */
    async removeQueryWorkspaceFile (queryId, relocatedToRepo = false) {
      await this.$store.dispatch(
        'queryWorkspace/removeQueryWorkspaceFile',
        {
          fileId: queryId,
          userId: this.userId
        }
      )

      if (!relocatedToRepo) {
        const tabIndex = this.tabs.findIndex(item => item.queryId === queryId)

        if (tabIndex >= 0) {
          this.onTabRemove(tabIndex)
        }
      }

      if (Object.values(this.scheduledQueries).find((scheduledQuery) => { return scheduledQuery.id === queryId })) {
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueries', this.userId)
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueriesSubscriptions', this.userId)
      }
    },

    /**
     * Saves Query File on the local/shared workspace
     * @param {String} queryName - The name of the query being saved
     * @param {Object} folder - The folder in which the query file is saved under
     */
    async saveQueryFile (queryName, folder) {
      /**
       * Get the data from the tab to save (by default "this.currentTab").
       * NOTE: If there's any new query to save and it was triggered the "Save All Tabs" function, then
       * get the data from the corresponding tab.
       */
      const tab = this.currentFileTreeSelector.tabIndex
        ? this.tabs[this.currentFileTreeSelector.tabIndex]
        : this.currentTab
      const query = {
        queryName,
        queryText: tab.editorText
      }

      if (folder.linkedToRepo) {
        try {
          const userFolder = await this.getQueryRepoUserFolder()
          this.saveQueryRepoFile(query, userFolder.id, tab)
        } catch (error) {
          console.error(`Error while saving query repo file: ${error}`)
          this.$Message.error({
            content: 'Error while creating user folder on Shared Workspace!',
            duration: 3
          })
        }
      } else if (folder.isRepo) {
        this.saveQueryRepoFile(query, folder.id, tab)
      } else {
        let folderPath = ''

        if (folder.isFolder) {
          folderPath = folder.filePath
        } else {
          const nodeFilePath = folder.filePath
          const nodeFilePathSplit = nodeFilePath.split('/')
          nodeFilePathSplit.pop()
          folderPath = nodeFilePathSplit.join('/')

          if (!folderPath) {
            folderPath = '/'
          }
        }

        const savedFileName = `${folderPath}${queryName}`
        this.addQueryWorkspaceFile(savedFileName, tab)
      }

      this.onFileTreeModalClose()
    },

    /**
     * Saves File on Query Repository
     * @param {Object} query - Query to be saved
     * @param {Number} folderId - The id of the folder in which the query will be saved in
     * @param {Object} tab - Tab object to be saved.
     */
    async saveQueryRepoFile (query, folderId, tab = this.currentTab) {
      try {
        const addedQuery = await this.addQueryRepoFile(query, folderId)
        this.$set(this.fileTreeSelector, 'savedFileName', query.queryName)
        this.afterAddQueryFile(addedQuery, tab, true, addedQuery.folderId)
      } catch (error) {
        console.error(`Error while adding file to Shared Workspace: ${error}`)
        this.$Message.error({
          content: 'Error while adding file to Shared Workspace!',
          duration: 3
        })
      }
    },

    /**
     * Creates a the user's personal folder in the query repository
     * @return {Promise} - Promise object that resolves with the user's personal folder on query repo
     */
    createQueryRepoUserFolder () {
      return this.$store.dispatch(
        'queryRepo/createQueryRepoUserFolder',
        {
          userId: this.userId,
          userEmail: this.$auth.getUser().email.toLowerCase()
        }
      )
    },

    /**
     * Retrieves the user's personal folder in the query repo. If it does not exist it will create one
     * and return it
     * @return {Promise} - Promise object that resolves with user's personal folder on query repo
     */
    getQueryRepoUserFolder () {
      return new Promise(async (resolve, reject) => {
        const userEmail = this.$auth.getUser().email.toLowerCase()
        const usersFolder = this.queryRepoFolders.find(folder => {
          const { name, editable, insertable } = folder

          return name.toLowerCase() === 'users' && !editable && !insertable
        })

        if (usersFolder) {
          const personalFolder = this.queryRepoFolders.find(folder => {
            const { name, parentId } = folder

            return name.toLowerCase() === userEmail && parentId === usersFolder.id
          })

          if (personalFolder) {
            resolve(personalFolder)
            return
          }
        }

        try {
          const result = await this.createQueryRepoUserFolder()
          resolve(result)
        } catch (error) {
          console.error(`Error while fetching query repo user folder: ${error}`)
          reject(error)
        }
      })
    },

    /**
     * Opens the File Tree Selector Modal
     * @param {String} mode - The mode of the File Tree Selector
     */
    openFileTreeModal (mode = '', name = this.currentTab.label) {
      this.$set(this.fileTreeSelector, 'queryObj', { name })
      this.$set(this.fileTreeSelector, 'mode', mode)
      this.$set(this.fileTreeSelector, 'visible', true)
    },

    /**
     * Closes the File Tree Selector Modal.
     */
    onFileTreeModalClose () {
      this.$set(this.fileTreeSelector, 'visible', false)
      this.$set(this.fileTreeSelector, 'fileName', '')
      this.$set(this.fileTreeSelector, 'queryObj', null)

      // Remove the first element from the "File Tree Selector Array" (in case there's any element).
      this.fileTreeSelectorArray.shift()

      // If there's any pending element to save, open a new modal to save the next tab.
      if (!_.isEmpty(this.currentFileTreeSelector)) {
        this.$nextTick(() => {
          this.openFileTreeModal(this.currentFileTreeSelector.mode, this.currentFileTreeSelector.name)
        })
      }
    },

    /**
     * Sets up the work area placing the existing tabs in LocalStorage.
     */
    setupWorkArea () {
      const storedTabs = JSON.parse(localStorage.getItem('storedTabs'))
      if (storedTabs.length) {
        this.tabs = []
        storedTabs.forEach(item => {
          item.isQueryWorkspace = item.queryId > 0 && item.queryPath !== null
          let labelId = item.labelId

          if (!item.isQueryWorkspace && !item.labelId) {
            const labelParts = item.label.split(' ')
            labelId = Number.parseInt(labelParts.pop())
          }

          const queryPath = !item.queryPath && item.isQueryWorkspace ? `/${item.label}` : (item.queryPath ? item.queryPath : null)
          const newTab = {
            id: item.id,
            label: item.label,
            queryPath: queryPath,
            editor: null,
            editorText: item.editorText,
            reference: item.reference,
            icon: item.queryHasChanged || !(item.isQueryWorkspace || item.isRepo) ? 'ios-medical' : '',
            isQueryWorkspace: item.isQueryWorkspace,
            queryId: item.queryId,
            labelId: labelId,
            undoIndex: 0,
            redoIndex: 0,
            query: '',
            hasQueryRun: false,
            isQueryRunning: false,
            results: [],
            showTable: false,
            editorHeight: this.editorHeight,
            statementId: '',
            sharedResultLinks: [],
            lastRunQueryId: null,
            isRepo: item.isRepo
          }
          this.tabs.push(newTab)
        })
      }

      this.initializeSharedLinkForTabs()

      // Verifies if there is any Shared Query to display
      const sharedQueryId = (this.$route.params.id && !this.$route.params.fileName) ? this.$route.params.id : null

      /**
       * If there isn't any Golden Query or Shared Query to display, then creates the first tab if there
       * isn't any existing stored into the localStorage. Then, exits the function.
       */
      if (!this.goldenQuery && !sharedQueryId) {
        if (!storedTabs.length) {
          this.createNewTab(0)
        }
        return
      }

      this.createExtraTab(parseInt(sharedQueryId))
    },

    /**
     * Creates an extra tab if there is any "Golden Query" or "Shared Query" to display.
     *   - If there is a Shared Query to display and it already exists in the tab panel, then exits.
     *   - If there is a Shared Query to display but it cannot be find into DB, then displays an error
     *     message and exits.
     * @param {Number} sharedQueryId
     */
    async createExtraTab (sharedQueryId) {
      let isSharedQuery = false
      let isSharedRepoQuery = false
      let sharedQuery = {}
      // if the link you clicked is refering a query in your workspace already, do not create a new query
      let ownQuery = null // optional query object for shared own query case

      if (sharedQueryId) {
        let queryInfo = null
        let sharedQueryLabel = null

        // If the query to share belongs to the Shared Workspace, fetch the query from there and set its
        // label name as its name.
        if (this.$route.path.includes('/shared-workspace/')) {
          isSharedRepoQuery = true
          queryInfo = (await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/query-repo/file`, {
            params: {
              queryId: sharedQueryId
            }
          })).data[0]
          sharedQueryLabel = queryInfo.queryName
        } else {
          queryInfo = (await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/queries`, {
            params: {
              queryId: [sharedQueryId]
            }
          })).data[0]

          if (!queryInfo) {
            return this.$Message.error(`No query with shared query ID ${sharedQueryId} found.`)
          }

          ownQuery = queryInfo.userId === this.userId ? queryInfo : null
          sharedQueryLabel = ownQuery
            ? `${ownQuery.queryName.split('/').slice(-1)}`
            : `Shared Query ${sharedQueryId}`
        }

        // Verify if the Shared Query already exists in the tab panel.
        if (isSharedRepoQuery) {
          this.sharedQueryTabIndex = this.tabs.findIndex(tab => tab.queryId === sharedQueryId && tab.isRepo)
        } else {
          this.sharedQueryTabIndex = this.tabs.findIndex(tab => tab.label === sharedQueryLabel)
        }

        if (this.sharedQueryTabIndex >= 0) {
          this.selectTab(this.sharedQueryTabIndex)
          return
        }

        if (!queryInfo) {
          const errorMessage = {
            title: 'Oops...',
            category: 'warning',
            message: `No query matches with the id <strong>${this.sanitize(sharedQueryId)}</strong>.`,
            cancelButton: {
              label: 'OK'
            }
          }
          this.showModalMixin(errorMessage)
          return
        }

        sharedQuery = {
          id: sharedQueryId,
          queryName: queryInfo.queryName,
          queryText: queryInfo.queryText,
          label: sharedQueryLabel
        }
        isSharedQuery = true
      }

      const labelId = this.getNewUnsavedTabIndex()
      const tabName = isSharedQuery
        ? sharedQuery.label
        : `Golden Query ${labelId}`
      const queryText = isSharedQuery
        ? sharedQuery.queryText
        : this.goldenQuery

      let queryObject = null

      if (isSharedRepoQuery) {
        queryObject = {
          queryId: sharedQuery.id,
          queryName: sharedQuery.queryName,
          queryText: sharedQuery.queryText,
          isQueryWorkspace: true,
          isRepo: true
        }
      } else {
        queryObject = {
          queryId: ownQuery && ownQuery.id,
          queryName: ownQuery && ownQuery.queryName,
          queryText: queryText,
          isQueryWorkspace: !!ownQuery // will not show unsaved icon if you are opening a link with a query from your workspace
        }
      }

      this.createNewTab(this.tabs.length, queryObject, tabName)
    },

    /**
     * Initializes the shared results links for each tab.
     */
    initializeSharedLinkForTabs () {
      const savedTabs = this.tabs.filter(tab => !!tab.queryId)

      savedTabs.forEach(this.initializeSharedLinks.bind(this))
    },

    /**
     * Initializes the shared link lists for the tab
     * @param {Object} tab - Tab object that we are retrieving links for
     */
    async initializeSharedLinks (tab) {
      const linkList = await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/shared-query-links`, {
        params: {
          queryId: tab.queryId
        }
      })

      tab.sharedResultLinks = linkList.data.links.map(link => {
        const { Key, LastModified } = link
        const linkSplit = Key.split('/')
        const fileName = linkSplit[linkSplit.length - 1]
        const lastModified = moment(LastModified)

        return this.createResultsLink(tab.queryId, fileName, lastModified)
      }).sort((linkA, linkB) => {
        const timeA = moment(linkA.created, 'MMM-DD-YYYY HH:mm:ss')
        const timeB = moment(linkB.created, 'MMM-DD-YYYY HH:mm:ss')

        if (timeA.isBefore(timeB)) return 1
        if (timeB.isBefore(timeA)) return -1

        return 0
      })
    },

    /**
     * Creates the result link object.
     * @param {String} queryId - The id of the query
     * @param {String} fileName - The file name of the shared query results
     * @param {Object} created - Moment object that represents the date and time the file was created
     */
    createResultsLink (queryId, fileName, created) {
      const createdFormatted = created.tz(moment.tz.guess()).format('MMM-DD-YYYY HH:mm:ss z')

      return {
        link: `${window.location.origin}/query-pad/${queryId}/${fileName}`,
        created: createdFormatted
      }
    },

    /**
     * Fetches a Query By Id.
     * @param {Number} queryId
     */
    fetchQuery (queryId) {
      return new Promise(async (resolve, reject) => {
        try {
          const query = await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/queries`, {
            params: {
              source: 'library',
              queryId: [queryId]
            }
          })

          resolve(query)
        } catch (err) {
          console.error(`Error while fetching query: ${err}`)
          reject(err)
        }
      })
    },

    /**
     * Fetches User's Query History and Library
     * @param {Number} userId
     */
    fetchQueryInfo () {
      Promise.all([this.fetchQueryWorkspace(), this.getQueryHistory(this.userId), this.fetchQueryRepo()])
        .then(results => {
          this.queryHistory = [...results[1]]
          this.onLoadQueryHistory()
        })
        .catch(error => {
          this.$Message.error(error)
        })
    },

    /**
     * Invokes Vuex action to fetch Query Repo
     */
    fetchQueryRepo () {
      this.$store.dispatch('queryRepo/fetchQueryRepo')
    },

    fetchQueryWorkspace () {
      this.$store.dispatch('queryWorkspace/fetchWorkspace', this.userId)
    },

    /**
     * Fetches User's Query History data.
     * @param {Number} userId
     * @return {Array}
     */
    async getQueryHistory (userId) {
      const response = await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/query-history`, {
        params: {
          userId: userId
        }
      })

      return response.data
    },

    /**
     * Updates the "Query History" ONLY if the panel height is higher than the original settings
     * and if there is more queries to display
     */
    debounceLoadQueryHistory: _.debounce(function () {
      let numLoad = this.calculateQueryLimit(this.panelHeight) - this.historyQueriesDisplayed
      if (numLoad < this.queryLoadMin) {
        numLoad = this.queryLoadMin
      }
      this.onLoadQueryHistory(numLoad)
    }, 1000),

    /**
     * Sets the items to display from history according with the maximum elements to display per block.
     * @param {Number} loadLimit
     */
    onLoadQueryHistory (loadLimit = this.queryLoadLimit) {
      return new Promise((resolve, reject) => {
        if (this.queryHistory.length === this.historyQueriesDisplayed) {
          return resolve()
        }

        const queryIdArr = []
        const loadedQueries = [...this.queryHistoryShown]
        let currentDate = loadedQueries.length > 0 ? loadedQueries[loadedQueries.length - 1].date : null
        const displayedQueries = this.historyQueriesDisplayed

        for (let i = displayedQueries; i < this.queryHistory.length && i < displayedQueries + loadLimit; i++) {
          const queryInfo = this.queryHistory[i]
          queryInfo.occuredAtTime = this.$moment(queryInfo.occuredAt).format('hh:mm:ss A')
          queryInfo.success = true
          queryInfo.queryText = ''
          queryIdArr.push(queryInfo.id)

          const itemDate = this.$moment(queryInfo.occuredAt).format('MMM DD, YYYY')
          if (currentDate !== itemDate) {
            currentDate = itemDate
            const newItem = {
              date: currentDate,
              items: []
            }

            loadedQueries.push(newItem)
          }

          loadedQueries[loadedQueries.length - 1].items.push(queryInfo)
          this.historyQueriesDisplayed++
        }

        if (queryIdArr.length) {
          axios.get(`${process.env.VUE_APP_API_URI}/query-pad/queries`, {
            params: {
              source: 'history',
              queryId: queryIdArr
            }
          })
            .then(results => {
              if (results.data.hasOwnProperty('error')) {
                throw (results.data.error)
              }

              results.data.forEach(result => {
                this.historyQueryIds[result.id] = result.queryText
              })

              loadedQueries.forEach(queryGroup => {
                queryGroup.items.forEach(query => {
                  query.queryText = this.historyQueryIds[query.id]
                })
              })

              this.queryHistoryShown = [...loadedQueries]
              resolve()
            })
            .catch(error => {
              console.error(`Error while loading query history: ${error}`)
              this.$Message.error(error)
              reject(error)
            })
        }
      })
    },

    /**
     * Calculates the number of items that will fit given, giving extra caution for margin of error.
     * @param {Number} height The height of the editor.
     * @return {Number} The number of items that will fit.
     */
    calculateQueryLimit (height) {
      const numFit = Math.ceil((height - 8 - 10 - 25) / this.historyItemHeight)
      return Math.max(numFit, this.queryLoadMin)
    },

    /**
     * Inserts the schema path from "Data Catalog" into the current pad.
     * NOTE: Once the path has been added, it will focus the pad placing the cursor at the end of the new string.
     * @param {String} path
     */
    addSchemaPath (path) {
      const cmInstance = this.getCodeMirror(this.currentTabIndex)
      const cursorPosition = cmInstance.getCursor()
      const doc = cmInstance.getDoc()
      doc.replaceRange(path, cursorPosition)
      cursorPosition.ch += path.length
      this.setFocus(cmInstance, cursorPosition)
    },

    /**
     * Gets the user selected query from CodeMirror.
     * If the the user highlighted text, the highlighted text is returned.
     * Otherwise, it finds the query where the cursor is located.
     * If between two queries, the previous query has priority.
     * @return {String} The query text without whitespaces at the front and end
     */
    getSelectedQuery () {
      const cm = this.$refs[this.currentTab.reference][0].codemirror

      if (cm.somethingSelected()) {
        return cm.getSelection()
      }

      const cursorPos = cm.getCursor()
      const beforeCursorText = cm.getRange({ line: 0, ch: 0 }, cursorPos)
      const afterCursorText = cm.getRange(cursorPos, { line: cm.lineCount(), ch: 0 })

      const isAfterSemicolon = /;/.test(beforeCursorText.charAt(beforeCursorText.length - 1))
      const isBeforeText = /\S/.test(afterCursorText.charAt(0))
      let isAfterSemicolonAndWhitespace = false

      const startIndex = Math.max(beforeCursorText.lastIndexOf(';') + 1, 0)
      const endIndex = afterCursorText.indexOf(';') + 1 || afterCursorText.length

      let queryHeadText = beforeCursorText.substring(startIndex).trimStart()
      if (!queryHeadText) {
        isAfterSemicolonAndWhitespace = true
        const prevQueryIndex = Math.max(beforeCursorText.lastIndexOf(';', startIndex - 2) + 1, 0)
        queryHeadText = beforeCursorText.substring(prevQueryIndex, startIndex)
      }

      const queryTailText = afterCursorText.substring(0, endIndex)

      let queryText = ''
      if (isAfterSemicolon || (isAfterSemicolonAndWhitespace && !isBeforeText)) {
        queryText = queryHeadText
      } else if (isAfterSemicolonAndWhitespace && isBeforeText) {
        queryText = queryTailText
      } else {
        queryText = queryHeadText + queryTailText
      }
      return queryText.trim()
    },

    /**
     * Runs the selected query existing in the current tab.
     */
    runQuery () {
      this.currentTab.isQueryRunning = true
      this.currentTab.hasQueryRun = false
      let editorText = this.getSelectedQuery()

      if (!editorText) {
        return
      }

      if (!this.currentTab.showTable) {
        this.currentTab.editorHeight = this.editorMinHeight
        this.$refs[this.currentTab.reference][0].codemirror.setSize(null, this.currentTab.editorHeight)
        this.currentTab.showTable = true
      }

      this.lastQueryRunAt = new Date().toISOString()
      this.$log('query_run', editorText)

      if (this.currentTab.query === editorText) {
        editorText += ' '
      }
      this.$nextTick(() => {
        this.currentTab.query = editorText
      })
    },

    /**
     * Cancels all of a user's running queries
     */
    cancelQueries () {
      this.tabs.forEach(tab => (tab.isQueryRunning = false))
      const userEmail = this.$auth.getUser().email
      axios({
        method: 'delete',
        url: `${process.env.VUE_APP_API_URI}/snowflake/queries?email=${userEmail}`
      }) // always returns 200
    },

    /**
     * Populate model based on query context for scheduling query from workspace
     * @param {Number} queryId
     * @param {Boolean} isWorkspaceFile
     * @param {Boolean} isRepo shared query
     */
    openScheduleQueryModal (queryId, isWorkspaceFile, isRepo) {
      const errorMessage = {
        title: 'Oops...',
        category: 'warning',
        cancelButton: {
          label: 'OK'
        }
      }
      if (!queryId || !isWorkspaceFile || isRepo) {
        errorMessage.message = isRepo ? 'Cannot schedule a shared query' : 'This query has not been saved yet. If you want to share it, please save it first.'
        this.showModalMixin(errorMessage)
        return
      } else if (this.currentTab.icon.length) {
        const queryName = this.currentTab.label
        errorMessage.message = `<strong>${this.sanitize(queryName)}</strong> query has been modified but its changes have not been saved yet. Please, save them first in order to share this query.`
        this.showModalMixin(errorMessage)
        return
      }
      this.showScheduleQueryModal = true
      const dataParam = {
        name: this.currentTab.label,
        text: this.currentTab.editorText,
        recurrence: 'Daily',
        emailSubscribers: [this.users.find(user => { return user.id === this.userId }).id],
        slackSubscribers: [this.users.find(user => { return user.id === this.userId }).id]
      }

      this.newScheduleQueryData = { ...this.newScheduleQueryData, ...dataParam }
    },

    closeScheduleQueryModal () {
      this.showScheduleQueryModal = false
      this.newScheduleQueryData = {
        name: '',
        schedule: '',
        recurrence: 'Daily',
        day: new Date().getDay(),
        hourAndMinute: '00:00',
        text: '',
        emailSubscribers: [],
        slackSubscribers: [],
        hasResults: false
      }
    },

    /**
     * Shows the Relocate Query Confirmation Modal
     * @param {Object} query - The query that is being relocated
     * @param {Object} folder - The destination folder
     */
    showRelocateQueryModal (query, folder) {
      const { queryName } = query
      const queryNameSplit = queryName.split('/')
      const queryTitle = queryNameSplit[queryNameSplit.length - 1]
      this.showModalMixin({
        visible: true,
        category: '',
        title: 'Copy File',
        message: `Would you like to leave a copy of <strong>${queryTitle}</strong> in your personal workspace?`,
        actionButtons: [{
          label: 'Yes',
          type: SUCCESS,
          action: 'copyAndRelocateToDrive',
          callback: () => {
            this.relocateQueryToRepo(query, folder, true)
            this.closeActionWindow()
          }
        }],
        cancelButton: {
          label: 'No',
          action: 'relocateToDrive',
          callback: () => {
            this.relocateQueryToRepo(query, folder)
            this.closeActionWindow()
          }
        }
      })
    },

    /**
     * Adds a Query Repo file
     * @param {Object} file - The query file about to be added
     */
    addQueryRepoFile (file, folderId) {
      return this.$store.dispatch('queryRepo/addQueryRepoFile', { file, folderId, userId: this.userId })
    },

    /**
     * Adds Folder to the Query Repository
     * @param {Object} folder - The folder being added
     * @param {String} parentId - The folder id in which the folder is saved under
     * @return {Promise} - Promise that resolves when the folder has been successfully added to repo
     */
    addQueryRepoFolder (folder, parentId) {
      return this.$store.dispatch('queryRepo/addQueryRepoFolder', { folder, parentId, userId: this.userId })
    },

    /**
     * Removes the query from the Query Repo
     * @param {Object} query - The query being removed
     */
    async removeQueryRepoFile (query) {
      try {
        await this.$store.dispatch('queryRepo/removeQueryRepoFile', {
          file: query,
          userId: this.userId
        })
      } catch (error) {
        const message = `Error while removing Shared Workspace File: ${error}`
        console.error(message)
        this.$Message.error({
          content: message,
          duration: 3
        })
      }
    },

    /**
     * Removes the folder from the Query Repo
     * @param {Object} folder - The folder being removed
     */
    async removeQueryRepoFolder (folder) {
      try {
        await this.$store.dispatch('queryRepo/removeQueryRepoFolder', {
          folder,
          userId: this.userId
        })
      } catch (error) {
        const message = `Error while removing Shared Workspace Folder: ${error}`
        console.error(message)
        this.$Message.error({
          content: message,
          duration: 3
        })
      }
    },

    /**
     * Change boolean value that represents if a query is running, used to change display of stop button
     * @param {Number} index Tab index to update
     * @param {Boolean} isQueryRunning
     */
    onRunningQueryChange (index, isQueryRunning) {
      if (!this.isSharedResult) {
        this.tabs[index].isQueryRunning = isQueryRunning
      }
    },

    /**
     * Clears the query pad in the current tab.
     */
    clearQuery () {
      if (this.currentTab.editorText) {
        this.currentTab.editorText = ''
      }
    },

    /**
     * Updated results from Query Pad
     * @param {Number} index Tab index to update
     * @param {Array} resultData Results for run queries
     * @param {Number} statementId The statement id of the query ran. Used by Snowflake for caching results
     */
    updateResults (index, resultData, statementId) {
      if (this.isSharedResult) {
        this.sharedQueryResult = resultData
      } else {
        this.tabs[index].isQueryRunning = false
        this.tabs[index].hasQueryRun = true
        this.tabs[index].results = resultData
        this.tabs[index].statementId = statementId
        if (!_.has(resultData[0], 'error')) {
          this.addToQueryHistory(index, resultData)
        }
      }
    },

    /**
     * Adds the executed query into the query history ONLY if the result was successful.
     * @param {Number} index Index of the associated TabPane
     * @param {Array} resultData
     */
    addToQueryHistory (index) {
      axios({
        method: 'post',
        url: `${process.env.VUE_APP_API_URI}/query-pad/query-history`,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          userId: this.userId,
          queryText: this.tabs[index].query.trim(),
          occuredAt: this.lastQueryRunAt,
          success: true
        }
      })
        .then(result => {
          const newQueryId = result.data[0].id
          const newQuery = {
            id: newQueryId,
            occuredAt: this.lastQueryRunAt,
            occuredAtTime: this.$moment(this.lastQueryRunAt).format('hh:mm:ss A'),
            success: true,
            queryText: this.tabs[index].query.trim()
          }

          const currentDate = this.$moment(this.lastQueryRunAt).format('MMM DD, YYYY')
          const dateExists = this.queryHistoryShown.length > 0 && this.queryHistoryShown[0].date === currentDate

          if (!dateExists) {
            const newItem = {
              date: currentDate,
              items: []
            }
            this.queryHistoryShown.unshift(newItem)
          }

          this.queryHistoryShown[0].items.unshift(newQuery)
          this.tabs[index].lastRunQueryId = newQuery.id

          this.resizeHeight()
        })
        .catch(error => {
          console.error(error)
        })
    },

    /**
     * Copies the existing query in floating preview into the current query pad.
     * @param {Object} queryObject
     * @param {Boolean} validateQuery   If "TRUE", the current query pad will be evaluated, before copy, to check if it has a content or not.
     * @param {Object} newTabProperties Specifies if it is needed to create a new tab and its name (if applies).
     */
    onCopyQueryToPad (queryObject, validateQuery, newTabProperties) {
      if (this.currentTab.editorText && validateQuery) {
        this.existingCode = {
          visible: true,
          icon: 'ios-warning-outline',
          title: 'Warning',
          class: 'warning',
          content: 'You have an existing code into the pad and it will be override. Do you want to copy it into a new tab or do want to proceed overriding the existing content?',
          queryText: queryObject.queryText
        }
        return
      }

      const { isRepo = false } = queryObject
      let tabIndex = -1

      if (isRepo) {
        tabIndex = this.tabs.findIndex(item => item.queryId === queryObject.queryId && item.isRepo)
      } else {
        tabIndex = this.tabs.findIndex(item => item.queryPath === queryObject.queryName)
      }

      if (tabIndex >= 0) {
        this.currentTabIndex = tabIndex
        const cmInstance = this.getCodeMirror(tabIndex)
        this.setFocus(cmInstance)
        return
      }

      this.existingCode.visible = false
      if (newTabProperties.createNewTab) {
        this.createNewTab(this.tabs.length, queryObject, newTabProperties.tabName)
      } else {
        this.currentTab.editorText = queryObject.queryText
      }
    },

    /**
     * Updates the localStorage var in order to store the content of every query pad with content.
     * NOTE: It will store each tab (with its properties) only if it has content in its query pad.
     */
    debounceLocalStorage: _.debounce(function () {
      this.setTabChangesHistory(this.currentTabIndex)
      const {
        queryId,
        editorText,
        isQueryWorkspace,
        isRepo
      } = this.currentTab
      let hasTextChanged = false

      if (isRepo) {
        const repoQuery = this.queryRepoFiles.find(query => query.id === queryId)

        hasTextChanged = repoQuery && repoQuery.queryText !== editorText
      } else {
        const workspaceQuery = this.queryWorkspace.find(query => query.id === queryId)

        hasTextChanged = workspaceQuery && workspaceQuery.queryText !== editorText
      }

      this.currentTab.icon = (hasTextChanged || !isQueryWorkspace) ? 'ios-medical' : ''
      this.updateLocalStorage()
    }, 500),

    /**
     * Updates an specific object from LocalStorage when a tab has been created or updated.
     */
    updateLocalStorage (tab = this.currentTab) {
      const {
        id,
        label,
        labelId,
        icon,
        editorText,
        queryId,
        queryPath,
        reference,
        isQueryWorkspace,
        isRepo
      } = tab
      let storedQuery = {}
      const queryHasChanged = icon.length > 0
      const storedTabs = JSON.parse(localStorage.getItem('storedTabs'))

      storedQuery = storedTabs.find(tab => tab.id === id)

      if (!storedQuery) {
        storedTabs.push({})
        storedQuery = storedTabs[storedTabs.length - 1]
      }

      this.$set(storedQuery, 'id', id)
      this.$set(storedQuery, 'label', label)
      this.$set(storedQuery, 'labelId', labelId)
      this.$set(storedQuery, 'editorText', editorText)
      this.$set(storedQuery, 'queryId', queryId)
      this.$set(storedQuery, 'queryPath', queryPath)
      this.$set(storedQuery, 'queryHasChanged', queryHasChanged)
      this.$set(storedQuery, 'reference', reference)
      this.$set(storedQuery, 'isQueryWorkspace', isQueryWorkspace)
      this.$set(storedQuery, 'isRepo', isRepo)
      localStorage.setItem('storedTabs', JSON.stringify(storedTabs))
    },

    /**
     * Updates the tabs information on Local Storage
     */
    updateLocalStorageTabs () {
      const storedTabs = this.tabs.map(tab => ({
        id: tab.id,
        label: tab.label,
        labelId: tab.labelId,
        icon: tab.icon,
        editorText: tab.editorText,
        queryId: tab.queryId,
        queryPath: tab.queryPath,
        reference: tab.reference,
        isQueryWorkspace: tab.isQueryWorkspace,
        isRepo: tab.isRepo
      }))

      localStorage.setItem('storedTabs', JSON.stringify(storedTabs))
    },

    /**
     * Check/Uncheck the provided option.
     * @param {String} option It could be: 'toolbar', 'hint', or 'panel'
     */
    toggleOption (option) {
      if (option.toLowerCase() === 'toolbar') {
        this.showToolBar = !this.showToolBar
        this.$set(this.$root.preferences.queryPad, 'showToolBar', this.showToolBar)
        this.resizeHeight()
        return
      } else if (option.toLowerCase() === 'hints') {
        this.showHints = !this.showHints
        this.$set(this.$root.preferences.queryPad, 'showHints', this.showHints)
        return
      } else if (option.toLowerCase() === 'lint') {
        this.cmOptions.lint = !this.cmOptions.lint
        this.$set(this.$root.preferences.queryPad, 'showLint', this.cmOptions.lint)
        return
      }

      this.showActionPanel = !this.showActionPanel
      this.$set(this.$root.preferences.queryPad, 'isShowActionPanel', this.showActionPanel)
      this.queryPanelLabel = `${(this.showActionPanel ? 'Hide' : 'Show')} Query Panel`
      const panelHasWidth = this.$refs['query-panel'].style.width

      if (panelHasWidth) {
        return
      }

      const panelMinWidth = `${this.queryPanelMinWidth}px`
      this.$refs['query-panel'].style.width = panelMinWidth
    },

    /**
     * Show codemirror hints for the current selected word.
     * It shows hints for snowflake database objects, allowed SQL keywords, and table/query aliases.
     *
     * @param {Object} cm The codemirror object for the current tab.
     */
    showHint (cm) {
      this.isHintShown = true
      cm.showHint({
        completeSingle: false,
        hint: function (codemirror, self) {
          const {
            selectedQuery,
            allowedKeywords,
            theme,
            cmThemes,
            snowflakeBlueprint,
            hintSearch,
            parseToken,
            dbSearch,
            keywordSearch,
            aliasSearch
          } = self

          const cursor = cm.getCursor()
          const cmToken = cm.getTokenAt(cursor)
          const start = cmToken.string.charAt(0) === '.' ? cmToken.start + 1 : cmToken.start
          const end = cursor.ch
          const line = cursor.line

          const tokenWords = parseToken(cm, cursor, cmToken)

          const aliasList = aliasSearch(tokenWords, selectedQuery, snowflakeBlueprint, hintSearch)
          const dbItemList = dbSearch(tokenWords, snowflakeBlueprint, hintSearch)
          const keywordList = keywordSearch(tokenWords, allowedKeywords, hintSearch)

          const hintList = [...aliasList, ...dbItemList, ...keywordList]

          return {
            list: hintList.map(result => ({
              text: result.name,
              render: function (Element, self) {
                const child = document.createElement('div')
                const hintType = document.createElement('p')
                const hintText = document.createElement('p')
                hintText.innerText = result.name
                hintType.innerText = result.type
                hintText.style = 'padding-right: 16px;'
                hintType.style = 'padding-left: 16px; opacity: 0.8; filter: alpha(opacity = 60); /* MSIE */'
                child.appendChild(hintText)
                child.appendChild(hintType)

                let itemStyle = 'display: flex; justify-content: space-between;'
                let listStyle = ''
                if (cmThemes.dark.includes(theme)) {
                  itemStyle += 'color: #f8f8f2;'
                  listStyle += 'border: 1; border-color: #444544; list-style-type: none; background-color: #282a36;'
                }
                child.style = itemStyle
                Element.appendChild(child)

                if (!self.isParentStyled && listStyle.length) {
                  Element.parentNode.style = listStyle
                  self.isParentStyled = true
                }
              }
            })),
            from: { line, ch: tokenWords.length ? start : end },
            to: { line, ch: end },
            theme,
            cmThemes,
            isParentStyled: false
          }
        },
        allowedKeywords: this.allowedKeywords,
        theme: this.cmOptions.theme,
        cmThemes: this.cmThemes,
        selectedQuery: this.getSelectedQuery(),
        snowflakeBlueprint: this.snowflakeBlueprint,
        parseToken: function (cm, cursor, token) {
          const tokenWords = []
          while (token.string.trim().length) {
            const currentWord = token.string.charAt(0) === '.' ? token.string.slice(1) : token.string
            tokenWords.unshift(currentWord)
            token = cm.getTokenAt({ line: cursor.line, ch: token.start })
          }
          return tokenWords
        },
        hintSearch: function (tokenWords, hints) {
          if (!tokenWords.length) {
            return hints
          }
          const lastWord = tokenWords[tokenWords.length - 1].toUpperCase()
          return hints.filter(hint =>
            hint.toUpperCase().indexOf(lastWord) >= 0
          ).sort((itemA, itemB) =>
            itemA.toUpperCase().indexOf(lastWord) - itemB.toUpperCase().indexOf(lastWord)
          )
        },
        dbSearch: function (tokenWords, dbItems, hintSearch) {
          for (let idx = 0; idx < tokenWords.length; idx++) {
            const word = tokenWords[idx]
            const wordSanitized = word.toUpperCase()

            if (!dbItems.hasOwnProperty(wordSanitized) || idx >= tokenWords.length - 1) {
              if (idx < tokenWords.length - 1) {
                dbItems = {}
              }
              break
            }
            if (dbItems.type.toUpperCase() === 'COLUMN') {
              dbItems = {}
              break
            }
            dbItems = dbItems[wordSanitized]
          }

          const dbItemList = hintSearch(tokenWords, Object.keys(dbItems)).map(item => ({
            name: item,
            type: dbItems.type.toUpperCase() === 'COLUMN' ? dbItems[item].type : dbItems.type.toUpperCase()
          }))

          return dbItemList
        },
        keywordSearch: function (tokenWords, allowedKeywords, hintSearch) {
          const keywordList = tokenWords.length > 1 ? [] : hintSearch(tokenWords, allowedKeywords).map(keyword => ({
            name: keyword.toUpperCase(),
            type: 'KEYWORD'
          }))

          return keywordList
        },
        aliasSearch: function (tokenWords, selectedQuery, dbItems, hintSearch) {
          if (tokenWords.length > 2) {
            return []
          }

          const curLint = new SQLLint(selectedQuery)
          curLint.lintCheck()
          const queryTokens = curLint.getTokenResults()

          let aliasList = []
          const aliasTokens = queryTokens.flat().filter(token => token.type === 'alias')

          if (!aliasTokens.length) {
            return []
          }

          if (tokenWords.length < 2) {
            const aliasTokensObj = aliasTokens.reduce((obj, token) => {
              obj[token.value] = { entity: token.entity, entityName: token.entityName }
              return obj
            }, {})
            aliasList = hintSearch(tokenWords, Object.keys(aliasTokensObj)).map(alias => ({
              name: alias,
              type: aliasTokensObj[alias].entity === 'subquery_chunk' ? 'SUBQUERY' : aliasTokensObj[alias].entityName.toUpperCase()
            }))
          } else {
            const token = aliasTokens.find(token => token.value === tokenWords[0])

            if (!token) {
              return []
            }

            if (tokenWords[0] === token.value) {
              const tablePathArr = token.entityName.toUpperCase().split('.')

              if (tablePathArr.length === 3 && _.has(dbItems, tablePathArr)) {
                const columnList = _.get(dbItems, tablePathArr)
                aliasList = hintSearch(tokenWords, Object.keys(columnList)).map(item => ({
                  name: item,
                  type: columnList[item].type
                }))
              }
            }
          }
          aliasList.sort((aliasA, aliasB) => aliasA.name - aliasB.name)

          return aliasList
        }
      })
    },

    /**
     * Sets properties for each codemirror component once they are ready (created).
     * @param {Number} index Tab index
     * @param {Object} cm
     */
    onCmReady (index, cm) {
      cm.setSize(null, this.tabs[index].showTable ? this.editorMinHeight : this.contentHeight)
      cm.on('keypress', _.debounce((cm, event) => {
        // 32: space, 59: semicolon
        if (!this.showHints || this.isHintShown || event.keyCode === 32 || event.keyCode === 59) {
          return
        }
        this.showHint(cm)
      }, 500))

      cm.on('endCompletion', () => {
        this.isHintShown = false
      })

      const doc = cm.getDoc()
      doc.clearHistory()

      if (!this.currentTabIndex) {
        const lastIndex = this.$root.preferences.queryPad.selectedTab || 0
        this.currentTabIndex = lastIndex >= this.tabs.length ? 0 : lastIndex
      }
      this.setFocus(cm)
    },

    /**
     * Get initial height location of codemirror component and add event listener to window and document body
     * @param {Object} $event
     */
    onHandleMouseDown ($event) {
      this.startY = $event.y
      this.startH = this.currentTab.editorHeight
      document.body.addEventListener('mousemove', this.onCmHandleDrag)
      window.addEventListener('mouseup', this.onCmHandleRelease)
    },

    /**
     * Sets height for each codemirror component based on drag event
     * @param {Object} $event
     */
    onCmHandleDrag ($event) {
      const dragDistance = $event.y - this.startY
      const newOrMinHeight = Math.max(this.startH + dragDistance, this.editorMinHeight)
      this.currentTab.editorHeight = Math.min(newOrMinHeight, this.editorHeight)
      this.$refs[this.currentTab.reference][0].codemirror.setSize(null, this.currentTab.editorHeight)
      this.resizeHeight()
    },

    /**
     * Event handler for handle release. Removes previous binded event listeners for document body and window
     */
    onCmHandleRelease () {
      this.$refs[this.currentTab.reference][0].codemirror.setSize(null, this.currentTab.editorHeight)
      document.body.removeEventListener('mousemove', this.onCmHandleDrag)
      window.removeEventListener('mouseup', this.onCmHandleRelease)
    },

    /**
     * Get initial height location of codemirror component and add event listener to window and document body
     * @param {Object} $event
     */
    onHandleMouseDownHorizontal ($event) {
      this.startX = $event.x
      this.startW = this.queryPanelWidth
      document.body.addEventListener('mousemove', this.onCmHandleDragHorizontal)
      window.addEventListener('mouseup', this.onCmHandleReleaseHorizontal)
    },

    /**
     * Sets height for each codemirror component based on drag event
     * @param {Object} $event
     */
    onCmHandleDragHorizontal ($event) {
      const dragDistance = $event.x - this.startX
      const halfWorkArea = (this.windowWidth - this.horizontalSpace) / 2
      const workAreaWithPreview = this.windowWidth - this.horizontalSpace - this.queryPanelWidth
      const queryPanelMaxWidth = Math.min(halfWorkArea, workAreaWithPreview)
      const newOrMinWidth = Math.max(this.startW + dragDistance, this.queryPanelMinWidth)
      this.queryPanelWidth = Math.min(newOrMinWidth, queryPanelMaxWidth)
    },

    /**
     * Event handler for handle release. Removes previous binded event listeners for document body and window
     */
    onCmHandleReleaseHorizontal () {
      document.body.removeEventListener('mousemove', this.onCmHandleDragHorizontal)
      window.removeEventListener('mouseup', this.onCmHandleReleaseHorizontal)
      this.$set(this.$root.preferences.queryPad, 'queryPanelWidth', this.queryPanelWidth)
    },

    /**
     * Opens a window to modify the title of a selected tab.
     */
    onRenameTab () {
      this.renameTabWindow.title = 'Rename Tab'
      this.renameTabWindow.icon = 'fas fa-edit'
      this.renameTabWindow.visible = true
      this.renameTabWindow.tabName = this.currentTab.label
      this.renameTabWindow.placeholder = 'Type a new name for tab'
      this.renameTabWindow.okButtonLabel = 'Rename'
      this.renameTabWindow.cancelButtonLabel = 'Cancel'
      this.renameTabWindow.isSaveQuery = false
      this.renameTabWindow.errorMessage = null
      this.$nextTick(() => {
        this.$refs.renameTabInput.$refs.input.focus()
      })
    },

    /**
     * Modifies the title of the selected tab.
     */
    renameTab () {
      const newTabName = this.renameTabWindow.tabName

      if (newTabName.includes('/')) {
        this.$set(this.renameTabWindow, 'errorMessage', `Slashes are not allowed!`)
        return
      }

      if (!this.currentTab.isQueryWorkspace) {
        const duplicatedTab = this.tabs.find(tab => {
          return tab.label.toLowerCase() === newTabName.toLowerCase() && !tab.isQueryWorkspace && tab !== this.currentTab
        })

        if (duplicatedTab) {
          this.$set(this.renameTabWindow, 'errorMessage', `There is an unsaved tab with the same name! Please, type a different one.`)
          return
        }
      }

      this.renameTabWindow.visible = false
      this.currentTab.label = newTabName
      this.updateLocalStorage()

      if (this.currentTab.isQueryWorkspace) {
        const queryFolder = this.currentTab.queryPath.split('/').slice(0, -1).join('/')
        this.currentTab.queryPath = `${queryFolder}/${this.currentTab.label}`
        const queryWorkspaceFile = this.queryWorkspace.find(query => query.id === this.currentTab.queryId)

        if (queryWorkspaceFile) {
          const queryToSave = {
            queryId: queryWorkspaceFile.queryId,
            queryName: this.currentTab.queryPath,
            queryText: queryWorkspaceFile.queryText
          }

          this.updateQueryWorkspaceFile(queryToSave, this.currentTab)
        } else {
          this.$Message.error('Unable to rename tab')
          console.error('Could not find query workspace file to update name')
        }
      }
    },

    /**
     * Configures the dialog box to sets the query name.
     * @param {Boolean} isSaveAs - True if option is Save As, false otherwise.
     * @param {Object} tab - Tab object to be saved/updated. If no one is provided, it will use the "currentTab" data.
     */
    async onSaveQuery (isSaveAs, tab = this.currentTab) {
      if (!isSaveAs) {
        if (tab.icon.length) {
          if (tab.isRepo) {
            const query = this.queryRepoFiles.find(queryFile => queryFile.id === tab.queryId)
            const queryToSave = { ...query }
            queryToSave.queryText = tab.editorText
            try {
              await this.updateQueryRepoFile(query, queryToSave, tab)
              this.$log('query_repo_file_update', queryToSave.queryText, { title: queryToSave.queryName, folderId: queryToSave.folderId })
            } catch (error) {
              console.error(`Error while saving Query on Repository: ${error}`)
              this.$Message.error({
                content: 'Error while Saving Query To Shared Workspace',
                duration: 3
              })
            }
          } else {
            try {
              const query = this.queryWorkspace.find(query => query.id === tab.queryId)
              const queryToSave = {
                queryId: query.id,
                queryName: query.queryName,
                queryText: tab.editorText
              }
              this.updateQueryWorkspaceFile(queryToSave, tab)
            } catch (error) {
              console.error(`Error while saving query on workspace: ${error}`)
              this.$Message.error({
                content: 'Error while Saving Query To Workspace',
                duration: 3
              })
            }
          }
        }
      } else {
        this.openFileTreeModal()
      }
    },

    /**
     * Saves all the unsaved queries (Workspace, Repo and New).
     */
    async onSaveAll () {
      this.fileTreeSelectorArray = []
      const workspaceTabsIndexArray = []

      // Verify if there is any unsaved query.
      this.tabs.forEach((tab, index) => {
        if (tab.icon.length) {
          // Create a list with all unsaved queries indexes from Workspace.
          if (tab.isQueryWorkspace || tab.isRepo) {
            workspaceTabsIndexArray.push(index)
          } else {
            // Create a list with all new unsaved queries.
            const fileTreeSelector = {
              tabIndex: index,
              name: tab.label,
              mode: ''
            }
            this.fileTreeSelectorArray.push(fileTreeSelector)
          }
        }
      })

      // Exit the function if there's no unsaved query.
      if (!this.fileTreeSelectorArray.length && !workspaceTabsIndexArray.length) {
        return
      }

      // Save all queries from Workspace (including the Repo Queries).
      workspaceTabsIndexArray.forEach(async (tabIndex) => {
        const tabToSave = this.tabs[tabIndex]
        await this.onSaveQuery(false, tabToSave)
      })

      // Render the "File Tree Selector" modal for the first new query to be saved.
      // NOTE: The pending queries will be render once the current one be closed.
      this.openFileTreeModal(this.currentFileTreeSelector.mode, this.currentFileTreeSelector.name)
    },

    /**
     * Updates the query on query repo
     * @param {Object} origFile - The original query file content
     * @param {Object} newFile - The file object with updated fields
     */
    updateQueryRepoFile (origFile, newFile, tab = this.currentTab) {
      return new Promise(async (resolve, reject) => {
        try {
          await this.$store.dispatch('queryRepo/updateQueryRepoFile', {
            origFile,
            newFile,
            userId: this.userId
          })

          tab.icon = ''
          this.updateLocalStorage(tab)
          resolve()
        } catch (error) {
          console.error(`Error while updating Query Repo File: ${error}`)
          reject(error)
        }
      })
    },

    /**
     * Opens the modal for saving a query to the Shared Workspace
     */
    openSaveToRepoModal () {
      this.openFileTreeModal(QUERY_REPO_MODE)
    },

    /**
     * Sanitizes a given string to avoid XSS.
     * @param {String} str
     * @return {String}  Sanitized string.
     */
    sanitize (str) {
      this.$refs.sanitized.textContent = str
      return this.$refs.sanitized.innerHTML
    },

    /**
     * Displays a confirm box to let the user be sure he/she wants to remove the selected query.
     */
    async onRemoveQuery () {
      const folderPath = this.selectedFileNode.folderPath === '/' ? 'My Workspace' : `My Workspace${this.selectedFileNode.folderPath.slice(0, -1)}`
      let message = ''
      let action = ''
      const affectedUsers = (await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/query-workspace/${this.selectedFileNode.query.id}/scheduled-queries/users`)).data

      if (this.selectedFileNode.isRepo) {
        message = `Are you sure you want to delete <strong>${this.sanitize(this.selectedFileNode.title)}</strong> from the <strong>Query Drive</strong>? (Note: This will delete the query for other users)`
        action = 'removeQueryRepoFile'
      } else {
        message = `Do you confirm to delete <strong>${this.sanitize(this.selectedFileNode.title)}</strong> query from <strong>${this.sanitize(folderPath)}</strong>?`
        if (affectedUsers.length) {
          message += `<p>Note the following user(s) have scheduled queries linked to it and will be affected:</p>`
          affectedUsers.forEach(user => {
            message += `<p> - ${user.email}</p>`
          })
        }
        action = 'removeQuery'
      }

      const errorMessage = {
        title: 'Delete Query',
        icon: 'fas fa-trash',
        category: 'custom',
        message,
        actionButtons: [
          {
            label: 'Confirm',
            type: WARNING,
            action
          }
        ],
        cancelButton: {
          label: 'Cancel'
        }
      }
      this.showModalMixin(errorMessage)
    },

    /**
     * Function to be called after a Query is added to local/shared workspace. Main purpose is to update the tabs
     * @param {Object} query - The query that was added
     * @param {Object} tab - Tab object to be saved/updated. If no one is provided, it will use the "currentTab" data.
     * @param {Boolean} isRepo - Indicates if query was added to the Shared Worksapce
     * @param {String} folderId - The id of the folder that the query was added to. Only applies to Shared Workspace
     */
    afterAddQueryFile (query, tab = this.currentTab, isRepo = false, folderId = null) {
      const { id: queryId, queryName } = query
      const fileName = queryName.split('/').pop()

      if (tab.isQueryWorkspace || tab.isRepo) {
        const queryObject = {
          queryId: queryId,
          queryName: `${queryName}`,
          queryText: tab.editorText,
          isQueryWorkspace: true,
          isRepo,
          folderId
        }

        this.createNewTab(this.tabs.length, queryObject, fileName)
      } else {
        this.updateCurrentTab(query, isRepo, tab)
      }
    },

    /**
     * Updates the current tab with information about a specific query
     * @param {Object} query - Query Object
     * @param {Boolean} isRepo - True if the query is within the repository. False if not.
     * @param {Object} tab - Tab object to be saved/updated. If no one is provided, it will use the "currentTab" data.
     */
    updateCurrentTab (query, isRepo = false, tab = this.currentTab) {
      const { id: queryId, queryName, folderId = null } = query
      const fileName = queryName.split('/').pop()

      tab.isQueryWorkspace = true
      tab.queryId = queryId
      tab.label = fileName
      tab.queryPath = queryName
      tab.icon = ''
      tab.isRepo = isRepo
      tab.folderId = folderId
      this.updateLocalStorage(tab)
    },

    // TODO: Use Vuex to move relocating logic to QueryPanel.vue

    /**
     * Relocates Query to Shared Workspace
     * @param {Object} query - Query that was relocated
     * @param {Object} folder - The folder in which the query was relocated to
     * @param {Boolean} saveCopy - Indicates whether or not to leave a copy of the query in the local workspace
     */
    async relocateQueryToRepo (query, folder, saveCopy = false) {
      const { id: folderId } = folder
      const queryToAdd = { ...query }
      const { queryName } = query
      const queryNameSplit = queryName.split('/')
      const queryTitle = queryNameSplit[queryNameSplit.length - 1]

      queryToAdd.queryName = queryTitle

      const addedQuery = await this.addQueryRepoFile(queryToAdd, folderId)

      if (saveCopy) {
        this.fetchQueryInfo()
      } else {
        this.removeQueryWorkspaceFile(query.id, true)
        // If relocated query is opened as a tab. Update the tab.
        const tab = this.tabs.find(queryTab => queryTab.queryId === query.id && !query.isRepo)

        if (tab) {
          this.$set(tab, 'queryId', addedQuery.id)
          this.$set(tab, 'isRepo', true)
          this.updateLocalStorageTabs()
        }
      }
    },

    /**
     * Relocates the Query within the shared workspace
     * @param {Object} query - The Query being relocated
     * @param {Object} folder - The folder that the query is relocated to
     */
    async relocateQueryWithinRepo (query, folder) {
      const { id: folderId } = folder
      const queryToSave = { ...query }

      queryToSave.folderId = folderId
      try {
        await this.updateQueryRepoFile(query, queryToSave)
        this.$log('query_repo_file_relocate', queryToSave.queryText, { title: queryToSave.queryName, folderId: queryToSave.folderId })
      } catch (error) {
        console.error(`Error while relocating query: ${error}`)

        this.$Message.error({
          content: 'Error while relocating query within Shared Workspace!',
          duration: 3
        })
        this.fetchQueryInfo(this.userId)
      }
    },

    /**
     * Sets the focus to the current codemirror ONLY if the window to modify a tab title has been closed.
     * @param {Boolean} isOpen
     */
    onRenameTabWindowChange (isOpen) {
      if (isOpen) {
        return
      }
      this.selectTab(this.currentTabIndex)
    },

    /**
     * Sets properties to current tab only if the selected tab isn't the "CREATE NEW TAB". Otherwise, it creates a new tab.
     * @param {Number} tabIndex The index of the tab.
     */
    onTabSelected (tabIndex) {
      if (tabIndex >= this.tabs.length) {
        this.createNewTab(tabIndex)
      } else if (!this.tabClick.click || this.tabClick.index !== tabIndex) {
        this.tabClick.click = true
        this.tabClick.timer = setTimeout((tabClick) => {
          tabClick.click = false
        }, this.tabClick.delay, this.tabClick)
        this.selectTab(tabIndex)
      } else {
        clearTimeout(this.tabClick.timer)
        this.tabClick.click = false
        this.onRenameTab(tabIndex)
      }
      this.tabClick.index = tabIndex
    },

    /**
     * Gets the codemirror instance of an specific tab.
     * @param {Number} tabIndex The index of the selected tab
     */
    getCodeMirror (tabIndex) {
      const queryTab = this.tabs[tabIndex]
      return this.$refs[queryTab.reference][0].cminstance
    },

    /**
     * Gets the codemirror instance of the selected tab and sets focus on it.
     * @param {Number} tabIndex The index of the selected tab
     */
    selectTab (tabIndex) {
      this.currentTabIndex = tabIndex
      const cmInstance = this.getCodeMirror(tabIndex)
      this.setFocus(cmInstance)
      this.$set(this.$root.preferences.queryPad, 'selectedTab', tabIndex)
    },

    /**
     * Gets the index for the new tab to be created.
     * @return {Number}
     */
    getNewUnsavedTabIndex () {
      /**
       * Create a list with the indexes of all "Untitled" and "Golden Query" tabs. This list will be useful
       * in order to avoid duplicated indexes when a new tab is created.
       */
      const unsavedTabIds = this.tabs.filter(tab => {
        const tabLabel = tab.label.toLowerCase()
        return (tabLabel.includes('untitled') || tabLabel.includes('golden query')) &&
          !isNaN((tabLabel.split(' ')).pop())
      })
        .map(tab => parseInt((tab.label.split(' ')).pop()))
        .sort((a, b) => b - a)

      return unsavedTabIds.length ? unsavedTabIds[0] + 1 : 1
    },

    /**
     * Creates a new tab.
     * NOTE: The new tab will be empty if "queryText" prop is empty. Otherwise, it will contains the "queryText" value.
     * @param {Number} tabIndex Index of the new tab (typically the current length of 'tabs')
     * @param {Object} queryObject Contains the query id ['queryId'] of the existing query (if applies) and the query that will be displayed in the editor ['queryEditor']
     * @param {String} tabName Defines the name of the tab (only for Golden Queries or when a query is stored in DB)
     */
    createNewTab (tabIndex, queryObject = {}, tabName = '') {
      const labelId = this.getNewUnsavedTabIndex()

      this.currentTabIndex = tabIndex
      const queryText = queryObject.queryText || 'SELECT * FROM <TABLE_NAME> WHERE <CONDITION> LIMIT 100;'
      const lastItemId = this.tabs.length ? this.tabs[this.tabs.length - 1].id + 1 : 1
      const labelName = tabName || `Untitled ${labelId}`
      const newTab = {
        id: lastItemId,
        label: labelName,
        queryPath: queryObject && queryObject.queryName ? queryObject.queryName : null,
        editor: null,
        originalQuery: queryText,
        editorText: queryText,
        reference: `editor_${lastItemId}`,
        isQueryWorkspace: queryObject.isQueryWorkspace || false,
        queryId: queryObject.queryId || null,
        labelId: labelId,
        undoIndex: 0,
        redoIndex: 0,
        query: '',
        hasQueryRun: false,
        isQueryRunning: false,
        results: [],
        showTable: false,
        editorHeight: this.editorHeight,
        icon: !queryObject.isQueryWorkspace ? 'ios-medical' : '',
        sharedResultLinks: [],
        lastRunQueryId: null,
        isRepo: queryObject.isRepo
      }

      if (newTab.queryId) {
        this.initializeSharedLinks(newTab)
      }

      this.tabs.push(newTab)
      if (labelName === `Untitled ${labelId}`) {
        this.formatQuery()
      }
      this.updateLocalStorage()
      this.$set(this.$root.preferences.queryPad, 'selectedTab', tabIndex)
    },

    /**
     * Sets the focus to a specific codemirror in a specific cursor position (if provided)
     * @param {Object} cmInstance
     * @param {Object} cursorPosition
     */
    setFocus (cmInstance, cursorPosition = {}) {
      const cursorLine = cursorPosition.line || cmInstance.lineCount()
      const cursorChar = cursorPosition.ch || 0
      this.$nextTick(() => {
        cmInstance.focus()
        cmInstance.setCursor(cursorLine, cursorChar)
        cmInstance.refresh()
      })
    },

    /**
     * If the selected tab has an unsaved query, it displays a popup to confirm the closing without save.
     * Othersiwe, it removes the selected tab.
     * @param {Number} tabIndex
     * @return {Promise}
     */
    beforeRemoveTab (tabIndex) {
      const cmInstance = this.getCodeMirror(tabIndex)
      const tabToClose = this.tabs[tabIndex]
      const message = (tabToClose.isQueryWorkspace || tabToClose.isRepo)
        ? `<p><b>${tabToClose.label}</b> query has unsaved changes. Do you want to close this tab without saving them?</p>`
        : `<p><b>${tabToClose.label}</b> query hasn't been saved before. Do you want to close this tab without save its content?</p>`
      return new Promise(resolve => {
        if (tabToClose.icon === 'ios-medical') {
          const confirmMessage = {
            title: 'Unsaved Query',
            category: 'warning',
            message,
            actionButtons: [
              {
                label: 'Confirm',
                type: WARNING,
                action: 'removeTab'
              }
            ],
            cancelButton: {
              label: 'Cancel'
            }
          }
          this.showModalMixin(confirmMessage)
          this.setFocus(cmInstance)
          this.currentTabIndex = tabIndex
        } else {
          this.onTabRemove(tabIndex)
          resolve()
        }
      })
    },

    /**
     * Removes tabs from the Tabs List one at time. If the current tab to analyze has an unsaved query,
     * it will display a popup to confirm the closing without save. Othersiwe, it removes the selected tab.
     * Once a tab is removed or skiped, it will analyze the next tab from the list.
     */
    removeMultiple () {
      if (!this.selectedTabsIndexArray.length) {
        return
      }

      const tabIndex = this.selectedTabsIndexArray[0]
      const tabToClose = this.tabs[tabIndex]
      const cmInstance = this.getCodeMirror(tabIndex)
      const message = (tabToClose.isQueryWorkspace || tabToClose.isRepo)
        ? `<p><b>${tabToClose.label}</b> query has unsaved changes. Do you want to close this tab without saving them?</p>`
        : `<p><b>${tabToClose.label}</b> query hasn't been saved before. Do you want to close this tab without save its content?</p>`

      this.currentTabIndex = tabIndex
      if (tabToClose.icon === 'ios-medical') {
        const confirmMessage = {
          title: 'Unsaved Query',
          category: 'warning',
          message,
          actionButtons: [
            {
              label: 'Confirm',
              type: WARNING,
              action: 'removeTabAndAnalyzeNextTab'
            },
            {
              label: 'Cancel',
              type: DEFAULT,
              action: 'analyzeNextTab'
            }
          ]
        }

        this.$nextTick(() => {
          this.showModalMixin(confirmMessage)
          this.setFocus(cmInstance)
        })
      } else {
        this.executeAction('removeTabAndAnalyzeNextTab')
      }
    },

    /**
     * Removes the query from the "tabs" array and updates the localStorage.
     * If the deleted tab is the last one, a new tab will automatically be added
     * @param {Number} tabIndex
     */
    onTabRemove (tabIndex) {
      this.tabs.splice(tabIndex, 1)
      const storedTabs = JSON.parse(localStorage.getItem('storedTabs'))
      storedTabs.splice(tabIndex, 1)
      localStorage.setItem('storedTabs', JSON.stringify(storedTabs))

      if (!this.tabs.length) {
        this.createNewTab(0)
      }

      if (this.currentTabIndex >= this.tabs.length) {
        this.currentTabIndex = this.tabs.length - 1
      }
    },

    /**
     * Copies the share link into the clipboard.
     * @param {String} item // Query ID
     * @param {Boolean} isWorkspaceFile
     * @param {Boolean} shareResult
     */
    async onShareLink (item, isWorkspaceFile, shareResult) {
      const queryName = this.currentTab.label
      const errorMessage = {
        title: 'Oops...',
        category: 'warning',
        cancelButton: {
          label: 'OK'
        }
      }
      if (!item || !isWorkspaceFile) {
        errorMessage.message = 'This query has not been saved yet. If you want to share it, please save it first.'
        this.showModalMixin(errorMessage)
        return
      } else if (this.currentTab.icon.length) {
        errorMessage.message = `<strong>${this.sanitize(queryName)}</strong> query has been modified but its changes have not been saved yet. Please, save them first in order to share this query.`
        this.showModalMixin(errorMessage)
        return
      } else if (shareResult && !this.currentTab.hasQueryRun) {
        errorMessage.message = `You requested to share the result of <strong>${this.sanitize(queryName)}</strong> query but you have not ran it yet.`
        this.showModalMixin(errorMessage)
        return
      }

      this.isCut = false

      if (shareResult) {
        this.$Message.loading({
          content: 'Creating Shared Results Link...',
          duration: 0
        })
        // make api call to save results csv to s3 then copy link.
        try {
          const result = await axios({
            method: 'post',
            url: `${process.env.VUE_APP_API_URI}/query-pad/shared-query-result`,
            timeout: 1000 * 60 * 5,
            headers: {
              'Content-Type': 'application/json'
            },
            data: {
              query: this.currentTab.query,
              queryId: this.currentTab.queryId,
              historyId: this.currentTab.lastRunQueryId,
              statementId: this.currentTab.statementId
            }
          })

          this.$Message.destroy()
          this.sharedQueryFileName = result.data
          const linkToShare = this.createResultsLink(item, this.sharedQueryFileName, moment(new Date()))
          this.currentTab.sharedResultLinks.unshift(linkToShare)
          this.$log('query_share_result', this.currentTab.editorText, { title: this.currentTab.label })
          this.copyLink(linkToShare.link)
        } catch (error) {
          const message = `Error while creating Shared Results Link: ${error}`

          console.error(message)
          this.$Message.destroy()
          this.$Message.error(message)
        }
      } else {
        const repoPath = this.currentTab.isRepo ? 'shared-workspace/' : ''
        const linkToShare = `${window.location.origin}/query-pad/${repoPath}${item}`
        this.$log('query_share', this.currentTab.editorText, { title: this.currentTab.label })
        this.copyLink(linkToShare)
      }
    },

    /**
     * Copies a link into the clipboard.
     * @param {String} link
     */
    copyLink (link) {
      this.$copyText(link).then(
        this.onCopyCutSuccess.bind(this),
        this.onCopyCutError.bind(this)
      )
    },

    /**
     * Copies/Cuts the selected text from a query, a line from cursor's position (if no selection)
     * or the whole query (for Shared Queries) into the clipboard.
     */
    copyQuery () {
      const cmInstance = this.getCodeMirror(this.currentTabIndex)
      const cursorPos = cmInstance.getCursor()
      const startLine = cursorPos.line
      const startChar = 0
      const endLine = this.isCut ? cursorPos.line + 1 : cursorPos.line
      const endChar = this.isCut ? 0 : cmInstance.getLine(cursorPos.line).length
      const lineSelection = cmInstance.getRange({ line: startLine, ch: startChar }, { line: endLine, ch: endChar })
      const selectedText = cmInstance.getSelection() || lineSelection

      if (selectedText === lineSelection) {
        cmInstance.setSelection({ line: startLine, ch: startChar }, { line: endLine, ch: endChar })
      }

      this.clipboardData = selectedText

      this.$log('query_copy', this.clipboardData)
      this.$copyText(this.clipboardData).then(
        this.onCopyCutSuccess.bind(this),
        this.onCopyCutError.bind(this)
      )
    },

    /**
     * Displays a success message if the copy/cut to clipboard worked.
     */
    onCopyCutSuccess () {
      const action = !this.isCut ? 'Copied' : 'Cut'
      this.$Message.success({
        content: `${action} to clipboard!`,
        duration: 3
      })

      if (this.isCut) {
        const cmInstance = this.getCodeMirror(this.currentTabIndex)
        const doc = cmInstance.getDoc()
        doc.replaceSelection('')
      }
    },

    /**
     * Displays an error message if the copy/cut to clipboard didn't worked.
     */
    onCopyCutError () {
      const action = !this.isCut ? 'copying' : 'cutting'
      this.$Message.error({
        content: `Error while ${action} to clipboard!`,
        duration: 3
      })
    },

    /**
     * Paste existing content in Clipboard into the current pad at cursor's position.
     */
    pasteQuery () {
      const cmInstance = this.getCodeMirror(this.currentTabIndex)
      const cursorPosition = cmInstance.getCursor()
      const doc = cmInstance.getDoc()
      doc.replaceRange(this.clipboardData, cursorPosition)
      cursorPosition.ch += this.clipboardData.length
      this.setFocus(cmInstance, cursorPosition)
    },

    setSnowflakeConfig (form) {
      this.$refs[form].validate((valid) => {
        if (!valid) {
          this.$Message.error('Missing Info!')
          return
        }

        this.$log('config_set', 'snowflake', this.setSnowflakeOptions(this.snowflakeCredentials))
        this.snowflakeConfig = Object.assign({}, this.snowflakeCredentials)
        this.$Message.success('Credentials set!')
        this.snowflakeConfigPage = false
      })
    },

    /**
     * Sets the Snowflake options according with the provided credentials
     * @param {Object} snowflakeCredentials
     */
    setSnowflakeOptions () {
      const snowflakeOptions = {}
      if (this.snowflakeCredentials.role) {
        snowflakeOptions.role = this.snowflakeCredentials.role
      }
      if (this.snowflakeCredentials.database) {
        snowflakeOptions.database = this.snowflakeCredentials.database
      }
      if (this.snowflakeCredentials.warehouse) {
        snowflakeOptions.warehouse = this.snowflakeCredentials.warehouse
      }
      return snowflakeOptions
    },

    /**
     * Sets the default credentials to use Snowflake.
     */
    setDefaultSnowflakeConfig () {
      this.snowflakeConfig = null
      this.$Message.success('Using default credentials!')
      this.snowflakeConfigPage = false
    },

    /**
     * Sets the proper indentation and the keywords in uppercase.
     */
    formatQuery () {
      this.currentTab.editorText = setUpQueryFormat(this.currentTab.editorText)
    },

    /**
     * Triggers the Undo/Redo commands to the current pad.
     * NOTE: Undo/Redo will be executed only if there is any changes to undo or redo.
     * @param {String} action undo/redo
     */
    undoRedoLastChange (action) {
      const cmInstance = this.getCodeMirror(this.currentTabIndex)
      const doc = cmInstance.getDoc()
      const tabData = this.currentTab

      if (action === 'undo' && tabData.undoIndex > 0) {
        doc.undo()
      } else if (action === 'redo' && tabData.redoIndex > 0) {
        doc.redo()
      }
    },

    /**
     * Keeps control about how many changes (undoes or redoes) does a codemirror has.
     * NOTE: This is useful in order to enable or disable the Undo/Redo options on menu bar.
     * @param {Number} tabIndex
     */
    setTabChangesHistory (tabIndex) {
      const cmInstance = this.getCodeMirror(tabIndex)
      const doc = cmInstance.getDoc()
      this.tabs[tabIndex].undoIndex = doc.historySize().undo
      this.tabs[tabIndex].redoIndex = doc.historySize().redo
    },

    /**
     * Triggers the corresponding search command on the current pad.
     * @param {String} command find, findNext, findPrev, replace, replaceAll
     */
    findReplace (command) {
      const cmInstance = this.getCodeMirror(this.currentTabIndex)
      cmInstance.execCommand(command)
    },

    /**
     * Called when the a query is being relocated
     * @param {Object} query - The query being relocated
     * @param {Object} folder - The folder that the query is relocated to. Only applicable if query is moved to
     * shared workspace
     */
    async onRelocateQueryFile (query, folder = null) {
      const { isRepo: queryIsRepo } = query
      const folderIsRepo = folder && folder.isRepo
      const isLinkedFolder = folder && folder.linkedToRepo
      let destinationFolder = folder

      if (isLinkedFolder) {
        try {
          destinationFolder = await this.getQueryRepoUserFolder()
        } catch (error) {
          console.error(`Error while creating user folder on query repo: ${error.message}`)
          this.$Message.error({
            content: 'Error while relocating query file to user folder',
            duration: 3
          })
        }
      }

      if ((folderIsRepo || isLinkedFolder) && queryIsRepo) {
        this.relocateQueryWithinRepo(query, destinationFolder)
      } else if (folderIsRepo || isLinkedFolder) {
        this.showRelocateQueryModal(query, destinationFolder)
      } else if (queryIsRepo) {
        this.relocateQueryToWorkspace(query)
      } else {
        this.relocateQueryWithinWorkspace(query)
      }
    },

    /**
     * Relocates Query from shared workspace to local workspace
     * @param {Object} query - The query being relocated
     */
    async relocateQueryToWorkspace (query) {
      const storedAtTime = new Date().toISOString()
      const { queryName, queryText } = query

      try {
        const result = await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-workspace`,
          data: {
            userId: this.userId,
            queryName,
            queryText,
            savedAt: storedAtTime
          }
        })
        const addedQuery = result.data

        // TODO: fix logic of afterAddQueryFile. Need to make it more generic to handle
        // different use cases (adding query files after saving, dropping, or clicking on save all
        // on the context menu). Can also look into separating into different functions that will
        // be called depending on situation. Ideally try to avoid hacky solutions of creating a fake
        // "tab" object to make it work properly.
        this.afterAddQueryFile(addedQuery, { isQueryWorkspace: true, editorText: query.queryText })
        this.fetchQueryInfo(this.userId)
      } catch (error) {
        const message = `Error while relocating query to workspace: ${error}`

        console.error(message)
        this.$Message.error({
          content: message,
          duration: 3
        })
        this.fetchQueryInfo(this.userId)
      }
    },

    /**
     * Relocates the query file within the personal workspace.
     * Note: The query parameter already contains updated location info
     * @param {Object} query - The relocated query object that is to be saved within database.
     */
    async relocateQueryWithinWorkspace (query) {
      const { id: queryId, queryName, queryText } = query

      await axios({
        method: 'patch',
        url: `${process.env.VUE_APP_API_URI}/query-pad/query-workspace`,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          userId: this.userId,
          queryId,
          queryName,
          queryText
        }
      })

      this.updateLocalStorage()
    },

    /**
     * Triggers a modal window to request a name for the new folder.
     */
    createNewFolder () {
      this.openFileTreeModal(FOLDER_MODE)
    },

    /**
     * Creates a new folder in the local workspace
     * @param {String} folderName - Name of folder being saved
     * @param {Array} folderPath - Array of folder names to indicate the folder path in which the folder is saved in
     */
    addWorkspaceFolder (folderName, folderPath) {
      if (folderPath[0] !== '') {
        folderPath.unshift('')
      }

      const folderPathStr = `${folderPath.join('/')}/`

      this.$store.commit('queryWorkspace/addFolder', {
        folderName,
        folderPath: folderPathStr
      })
    },

    /**
     * Triggers a modal window to confirm folder removing.
     */
    removeFolder () {
      const { isRepo = false, children = [] } = this.selectedFileNode
      let message = ''
      let actionButtons = []

      if (children.length > 0) {
        message = `The <strong>${this.sanitize(this.selectedFileNode.title)}</strong> folder contains items. Please empty the folder before deleting it.`
      } else if (isRepo) {
        const { editable, userId } = this.selectedFileNode.folder

        if (!editable || (userId && userId !== this.userId)) {
          message = `You do not have the permissions to delete <strong>${this.sanitize(this.selectedFileNode.title)}</strong> folder.`
        } else {
          message = `
            Are you sure you want to delete <strong>${this.sanitize(this.selectedFileNode.title)}</strong> folder from the <strong>Shared Workspace</strong>?
            This action will affect other users.
          `
          actionButtons = [
            {
              label: 'Confirm',
              type: WARNING,
              action: 'removeQueryRepoFolder'
            }
          ]
        }
      } else {
        message = `Do you confirm to delete <strong>${this.sanitize(this.selectedFileNode.title)}</strong> folder?`
        actionButtons = [
          {
            label: 'Confirm',
            type: WARNING,
            action: 'removeFolder'
          }
        ]
      }

      const errorMessage = {
        title: 'Delete Folder',
        category: 'custom',
        icon: 'fas fa-trash',
        message,
        actionButtons,
        cancelButton: {
          label: 'Cancel'
        }
      }
      this.showModalMixin(errorMessage)
    },

    /**
     * Closes the remove folder modal window and turn on the "removeFolder" flag.
     * When the "removeFolder" flag is on, it triggers the "removeFolder" watch function into
     * the "QueryPanel" component in order to try to remove the selected folder from the "Library" section
     * NOTES:
     * - If the folder is empty, it will be removed.
     * - If the folder is not empty, it will be triggered an error message to let the user know that the
     *   folder should be empty before to remove it.
     */
    onRemoveFolder () {
      let folderPathStr = this.selectedFileNode.folderPath

      if (folderPathStr[folderPathStr.length - 1] === '/') {
        folderPathStr = folderPathStr.substring(0, folderPathStr.length - 1)
      }

      const folderPathSplit = folderPathStr.split('/')
      const folderName = folderPathSplit.pop()
      const folderPath = folderPathSplit.join('/') + '/'

      this.$store.commit('queryWorkspace/removeFolder', {
        folderName,
        folderPath
      })
    },

    /**
     * Executes an action depending of the menu option selected.
     * @param {String} option
     */
    onMenuClick (option, value = null) {
      const queryId = this.currentTab.queryId
      const isWorkspaceFile = this.currentTab.isQueryWorkspace

      switch (option) {
        case 'newTab':
          this.createNewTab(this.tabs.length)
          break
        case 'removeFile':
          this.onRemoveQuery()
          break
        case 'newFolder':
          this.createNewFolder()
          break
        case 'removeFolder':
          this.removeFolder()
          break
        case 'save':
          this.onSaveQuery(!this.currentTab.isQueryWorkspace)
          break
        case 'saveAs':
          this.onSaveQuery(true)
          break
        case 'saveToRepo':
          this.openSaveToRepoModal()
          break
        case 'saveAll':
          this.onSaveAll()
          break
        case 'closeQuery':
          this.beforeRemoveTab(this.currentTabIndex)
          break
        case 'closeSaved':
          this.selectedTabsIndexArray = []
          this.tabs.forEach((tab, index) => {
            if (tab.icon === '') {
              this.selectedTabsIndexArray.push(index)
            }
          })
          this.removeMultiple()
          break
        case 'closeOthers':
        case 'closeAll':
          this.selectedTabsIndexArray = []
          this.tabs.forEach((tab, index) => {
            if (option === 'closeAll' || index !== this.currentTabIndex) {
              this.selectedTabsIndexArray.push(index)
            }
          })
          this.removeMultiple()
          break
        case 'copyResult':
          this.$refs[`queryResults_${this.currentTabIndex}`][0].exportData('clipboard')
          break
        case 'downloadAllData':
          this.$refs[`queryResults_${this.currentTabIndex}`][0].exportData('csvAll')
          break
        case 'googleSheet':
          this.$refs[`queryResults_${this.currentTabIndex}`][0].exportData('spreadsheet')
          break
        case 'googleScript':
          this.$refs[`queryResults_${this.currentTabIndex}`][0].exportData('appscript')
          break
        case 'exportEmail':
          this.$refs[`queryResults_${this.currentTabIndex}`][0].exportData('email')
          break
        case 'undo':
        case 'redo':
          this.undoRedoLastChange(option)
          break
        case 'find':
        case 'findNext':
        case 'findPrev':
        case 'replace':
        case 'replaceAll':
          this.findReplace(option)
          break
        case 'copy':
        case 'cut':
          this.isCut = option === 'cut'
          this.copyQuery()
          break
        case 'paste':
          this.pasteQuery()
          break
        case 'clear':
          this.clearQuery()
          break
        case 'toolbar':
        case 'panel':
        case 'hints':
        case 'lint':
          this.toggleOption(option)
          break
        case 'indent':
          this.formatQuery()
          break
        case 'lineComment':
          this.toggleLineComment(this.currentTabIndex)
          break
        case 'blockComment':
          this.toggleBlockComment(this.currentTabIndex)
          break
        case 'snowflake':
          this.snowflakeConfigPage = true
          break
        case 'run':
          this.runQuery()
          break
        case 'cancel':
          this.cancelQueries()
          break
        case 'schedule':
          this.openScheduleQueryModal(this.currentTab.queryId, this.currentTab.isQueryWorkspace, this.currentTab.isRepo)
          break
        case 'share':
          this.onShareLink(queryId, isWorkspaceFile)
          break
        case 'shareResult':
          this.onShareLink(queryId, isWorkspaceFile, true)
          break
        case 'sharedLinkSelect':
          if (value) {
            this.copyLink(value)
          }
          break
        case 'feedback':
          this.$feedback('data-eng-team@li.me', 'Help on Query Pad', 'query pad', this.$route.path)
          break
        case 'sqlLint':
          this.lintCheck()
          break
        case 'nominate':
          this.onCheckQueryRun()
          break
        case 'drawPolygon':
          this.drawingPolygon = true
          break
        default:
          if (option.includes('theme')) {
            const newTheme = option.replace('theme-', '')
            this.cmOptions.theme = newTheme
            this.$set(this.$root.preferences.queryPad, 'cmTheme', newTheme)
          }
      }
    },

    /**
     * Do lint Checking, trigger by checking button
     */
    lintCheck () {
      const rawText = this.currentTab.editorText
      const sqlLint = new SQLLint(rawText)
      const lintMessageArray = sqlLint.lintCheck()
      if (lintMessageArray.length) {
        const alertMessage = lintMessageArray.reduce((acc, cur) => acc + `\n${cur.content}`, '')
        alert(alertMessage)
      } else {
        alert('No error or warning detected for current lint rules.')
      }
    },

    /**
     * Check if the query has already been run before nomination
     */
    onCheckQueryRun () {
      const results = this.currentTab.results
      if (_.has(results[0], 'error')) {
        this.$Message.error({
          content: 'Incorrect Query, try fix or test agian before moving on.',
          duration: 3
        })
      } else if (!results.length) {
        const warnMessage = {
          closable: true,
          maskClosable: true,
          title: 'Query Correctness Check',
          category: 'warning',
          message: 'Query not run yet. Better run before nomination.',
          actionButtons: [
            {
              label: 'Proceed Anyway',
              type: WARNING,
              callback: this.nominateQuery
            }
          ],
          cancelButton: {
            label: 'Go Back to Run Query'
          }
        }
        this.showModalMixin(warnMessage)
      } else if (this.currentTab.icon.length) {
        const warnMessage = {
          closable: true,
          maskClosable: true,
          title: 'Query Modified',
          category: 'warning',
          message: 'Query may be modified or not saved. Better save and test it before nomination.',
          actionButtons: [
            {
              label: 'Proceed Anyway',
              type: WARNING,
              callback: this.nominateQuery
            }
          ],
          cancelButton: {
            label: 'Go Back'
          }
        }
        this.showModalMixin(warnMessage)
      } else {
        this.$Message.success('Query is run and is correct, now proceed to nominate!')
        this.nominateQuery()
      }
    },

    /**
     * Nominate query in current tab to be a template in query community
     */
    nominateQuery () {
      const rawQuery = this.currentTab.editorText
      const name = this.currentTab.label
      this.$router.push({
        name: 'Query Template Wizard',
        params: {
          queryText: rawQuery,
          queryName: name
        }
      })
    },

    /**
     * Sets the data for interaction window to render with
     * @param {Object} windowData - Data used for interaction window
     */
    setInteractionWindowData (windowData = {}) {
      const {
        visible = this.interactionWindow.visible,
        icon = this.interactionWindow.icon,
        title = this.interactionWindow.title,
        message = this.interactionWindow.message,
        inputName = this.interactionWindow.inputName,
        placeholder = this.interactionWindow.placeholder,
        confirmLabel = this.interactionWindow.confirmLabel,
        confirmOption = this.interactionWindow.confirmOption,
        cancelOption = this.interactionWindow.cancelOption,
        cancelLabel = this.interactionWindow.cancelLabel,
        hasError = this.interactionWindow.hasError,
        errorMessage = this.interactionWindow.errorMessage
      } = windowData

      this.interactionWindow = {
        visible,
        icon,
        title,
        message,
        inputName,
        placeholder,
        confirmLabel,
        confirmOption,
        cancelOption,
        cancelLabel,
        hasError,
        errorMessage
      }
    },

    /**
     * Closes the interaction window and initializes its values.
     */
    interactionWindowReset () {
      this.setInteractionWindowData({
        visible: false,
        icon: '',
        title: '',
        message: '',
        inputName: '',
        placeholder: '',
        confirmLabel: '',
        confirmOption: '',
        cancelOption: '',
        cancelLabel: '',
        hasError: false,
        errorMessage: ''
      })
    },

    /**
     * Toggles a line comment to the selected lines.
     * @param {Number} tabIndex
     */
    toggleLineComment (tabIndex) {
      const cmInstance = this.getCodeMirror(tabIndex)
      cmInstance.execCommand('toggleComment')
    },

    /**
     * Toggles a block comment to the selected lines.
     * @param {Number} tabIndex
     */
    toggleBlockComment (tabIndex) {
      const cmInstance = this.getCodeMirror(tabIndex)
      const selectionStartPosition = cmInstance.getCursor(true)
      const selectionEndPosition = cmInstance.getCursor(false)
      const isCommentBlockStart = cmInstance.getLine(selectionStartPosition.line).includes('/*')
      const isCommentBlockEnd = cmInstance.getLine(selectionEndPosition.line).includes('*/')

      if (isCommentBlockStart && isCommentBlockEnd) {
        cmInstance.uncomment(selectionStartPosition, selectionEndPosition)
      } else {
        cmInstance.blockComment(selectionStartPosition, selectionEndPosition)
      }
    },

    /**
     * Callback for when include result checkbox is checked
     * @param {Boolean} includeResult - true if include result, false if not.
     */
    onIncludeResultCheck (includeResult) {
      this.includeResult = includeResult
    },

    /**
     * Triggers the corresponding action (function) according with the provided option.
     * @param {String} action
     */
    executeAction (action) {
      if (!action) {
        return
      }

      switch (action) {
        case 'removeQuery':
          this.removeQueryWorkspaceFile(this.selectedFileNode.query.id)
          break
        case 'removeFolder':
          this.onRemoveFolder()
          break
        case 'removeQueryRepoFolder':
          this.removeQueryRepoFolder(this.selectedFileNode.folder)
          break
        case 'removeTab':
          this.onTabRemove(this.currentTabIndex)
          break
        case 'removeTabAndAnalyzeNextTab':
        case 'analyzeNextTab':
          if (action === 'removeTabAndAnalyzeNextTab') {
            this.onTabRemove(this.currentTabIndex)
            this.selectedTabsIndexArray = this.selectedTabsIndexArray.map(index => index - 1)
          }

          // Remove the first element from the list in order to not analyze it again.
          this.selectedTabsIndexArray.splice(0, 1)

          // Analyze next tab to remove
          this.removeMultiple()
          break
        case 'removeQueryRepoFile':
          this.removeQueryRepoFile(this.selectedFileNode.query)
          break
        default:
      }

      this.closeActionWindow()
    },

    /**
     * Closes the action window (modal).
     */
    closeActionWindow () {
      this.hideModalMixin()
    },

    /**
     * Patch geospatial conditions to current query
     * @param {String} conditions represents WHERE conditions from PolygonDrawingModel including minimal square & encoded geo array string
     */
    onPatchingGeospatialConditions (conditions) {
      const editorText = this.getSelectedQuery()
      this.currentTab.editorText = `${editorText}\n${conditions}`
    },

    /**
     * Create a new tab for sample geospatial query
     * @param {String} query represents sample query text from PolygonDrawingModel
     */
    onExportSampleGeospatialQuery (query) {
      const queryObject = {
        queryText: query
      }

      this.createNewTab(this.tabs.length, queryObject)
    },

    /**
     * Executes show/hide Polygon Drawing Modal
     * @param {Boolean} value represents updated value from Polygon Drawing Modal for show/hide
     */
    onPolygonDrawing (value) {
      this.drawingPolygon = value
    }
  }
}
</script>

<style scoped>
  .no-data-msg {
    text-align: center;
    font-size: 32px;
    margin-top: 15%;
    margin-bottom: 15%;
  }

  .shared-query {
    margin-top: 32px;
  }

  .pad-container {
    position: relative;
    margin-top: 0px;
    padding-bottom: 0px;
  }

  .toolbar-container {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    z-index: 1;
    align-items: baseline;
  }

  .toolbar-container.preview {
    justify-content: flex-end;
  }

  .pad-icon-container {
    margin-right: 8px;
  }

  .pad-icon {
    margin-right: 8px;
    cursor: pointer;
  }

  .pad-icon-fa {
    font-size: 20px;
    vertical-align: middle;
  }

  .pad-button-container {
    margin-left: 8px;
  }

  .query-editor {
    border-style: solid;
    border-width: 1px;
    border-color: var(--color-gray-border);
    border-top: none;
    border-bottom: none;
  }

  .interaction-window-title {
    text-align: center;
  }

  .interaction-window-title .window-icon,
  .interaction-window-title .window.title {
    font-size: 16px;
  }

  .interaction-window-message {
    font-size: 14px;
  }

  .credentials-page-info {
    text-align: center;
  }

  .snowflake {
    color: var(--color-blue-snowflake);
  }

  .alert {
    color: var(--color-red-alert);
  }

  .snowflake-input-divider {
    height: inherit;
    margin-left: 8px;
    margin-right: 8px;
  }

  .snowflake-credentials-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .snowflake-options-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .tab-layout {
    height: fit-content;
    position: relative;
    display: flex;
  }

  .action-panel {
    box-sizing: border-box;
    border: 1px solid var(--color-gray-border);
    background-color: transparent;
    width: 152px;
  }

  .action-panel .query-panel {
    height: 100%;
    background-color: var(--color-white);
  }

  .action-panel .query-panel >>> .main-tab.ivu-tabs {
    height: 100%;
  }

  .content-panel {
    width: 100%;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .footer-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .warning {
    color: var(--color-red-alert);
  }

  /* For CodeMirror Handle */
  .handle {
    background: var(--color-light-gray);
    height: 20px;
    user-select: none;
    cursor: row-resize;
  }

  .handle:before,
  .handle.vertical:before {
    content: '\2261'; /* https://en.wikipedia.org/wiki/Triple_bar */
    color: var(--color-mid-gray);
    position: absolute;
    left: 50%;
    line-height: 16px;
    font-size: 200%;
  }

  .handle.vertical:before {
    transform: rotate(90deg);
  }

  .handle:hover {
    background: var(--color-gray-hover);
  }

  .handle:hover:before {
    color: var(--color-black);
  }

  .handle.vertical {
    position: relative;
    cursor: col-resize;
    height: auto;
    width: 16px;
    border-top: 0;
    border-bottom: 1px solid var(--color-gray-border);
  }
  .handle.vertical:before {
    top: 50%;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .handle.horizontal {
    border: 1px solid var(--color-gray-border);
    position: relative;
  }

  .button-icon {
    padding-right: 8px;
  }

  .copy-action-button {
    width: 90px;
  }

  .query-editor >>> .CodeMirror pre.CodeMirror-placeholder {
    color: var(--color-mid-gray);
  }

  .query-tabs >>> .ivu-tabs-bar {
    margin-bottom: 0;
    user-select: none;
  }

  .query-tabs >>> .ivu-tabs-tab .ivu-icon {
    color: var(--color-red);
    font-size: 10px;
    margin: 0;
  }

  .query-tabs >>> .ivu-tabs-content {
    padding-top: 0;
    color: var(--color-black);
    background-color: var(--color-white);
  }

  .query-tabs >>> .CodeMirror {
    height: 100% !important;
  }

  .query-tabs >>> .ivu-tabs-nav-container {
    font-size: 12px;
  }

  .query-tabs >>> .v-context {
    margin: 0;
    padding: 0;
    position: absolute;
    z-index: 9999;
    list-style: none;
    background-color: var(--color-light-gray);
    border: 1px solid var(--color-gray-border);
    border-radius: 4px;
  }

  .query-tabs >>> .v-context .separator {
    border-top: 1px solid var(--color-gray-border)
  }

  .query-tabs >>> .v-context p {
    margin: 0;
    padding: 2px 8px;
    cursor: default;
    font-weight: bold;
  }

  .query-tabs >>> .v-context p:first-child {
    padding-top: 4px;
  }

  .query-tabs >>> .v-context p:last-child {
    padding-bottom: 4px;
  }

  .query-tabs >>> .v-context p:hover {
    color: var(--color-white);
    background-color: var(--color-info);
  }

  .hidden {
    display: none;
  }

  .lint-message-wrapper {
    background-color: var(--color-white);
    color: var(--color-black);
    height: auto;
    min-height: 30px;
    width: inherit;
    padding: 5px;
    border: 1px solid var(--color-gray-border);
    border-bottom: unset;
    position: relative;
  }

  .action-icon {
    padding: 5px;
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
  }

  .timezone {
    color: var(--color-gray);
  }

  .file-tree-selector-modal >>> .tree-node-disabled {
    opacity: 0.5;
  }

  .error {
    padding-top: 8px;
    font-weight: bold;
    color: var(--color-red-border);
    text-align: justify;
  }
</style>
